import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { api } from '../api';
import SelectColaborador from '../components/SelectColaborador';
import ModalConfirmar from '../components/ModalConfirmar';
import PainelLancamentos from '../components/PainelLancamentos';
import { ModalDemanda, ModalDesfechoDemanda, urgencia } from './Demandas';
import AgendarMensagemCliente from '../components/AgendarMensagemCliente';
import HistoricoMensagens from '../components/HistoricoMensagens';
import OportunidadesCliente from '../components/OportunidadesCliente';
import { colarEmMaiusculas } from '../colarMaiusculas';

const PROCESSO_VAZIO = {
  numero_processo: '', nome_marca: '', situacao: '', apresentacao: '',
  natureza: '', classes: '', deposito: '', concessao: '', vencimento: '',
  titular: '', procurador: '',
  // De qual cadastro é o processo (22/10/2026): '' = principal (aba Identificação);
  // senão o id de uma empresa da aba Outros (outros_cadastros)
  cadastro_id: '',
};
const processoVazio = p => Object.keys(PROCESSO_VAZIO).every(k => k === 'cadastro_id' || !String(p?.[k] || '').trim());

const CAMPOS_PROCESSO = [
  { name: 'numero_processo', label: 'Número do Processo', placeholder: 'Ex: 935613161', col: 1 },
  { name: 'nome_marca',      label: 'Nome da Marca',       placeholder: 'Nome da marca registrada', col: 1 },
  { name: 'situacao',        label: 'Situação',            placeholder: 'Ex: Registro de marca em vigor', col: 2 },
  { name: 'titular',         label: 'Titular',             placeholder: 'Nome do titular', col: 1 },
  { name: 'procurador',      label: 'Procurador',          placeholder: 'Nome do procurador', col: 1 },
  { name: 'apresentacao',    label: 'Apresentação',        placeholder: 'Ex: Mista', col: 1 },
  { name: 'natureza',        label: 'Natureza',            placeholder: 'Ex: Produtos e/ou Serviço', col: 1 },
  { name: 'classes',         label: 'Classe de Nice',      placeholder: 'Ex: NCL(12) 45', col: 1 },
  { name: 'deposito',        label: 'Data de Depósito',    placeholder: 'dd/mm/aaaa', col: 1 },
  { name: 'concessao',       label: 'Data de Concessão',   placeholder: 'dd/mm/aaaa', col: 1 },
  { name: 'vencimento',      label: 'Data de Vigência',    placeholder: 'dd/mm/aaaa', col: 1 },
];

const FORM_VAZIO = {
  // Identificação
  nome: '', razao_social: '', cpf: '', rg: '', orgao: '', cnpj: '',
  data_nascimento: '', data_contrato: '',
  inscricao_municipal: '', inscricao_estadual: '',
  // Contato
  telefone: '', email: '', email_financeiro: '',
  responsavel: '', po: '',
  // Endereço
  cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
  referencia: '',
  // Comercial
  shopping: '', contador: '',
  programa: '', atividade: '', tipo_pagamento: '',
  consultor: '',
  indicacao: '', avaliacao: '',
  financeiro: '', regime_tributario: '',
  horario_seg_sex: '', horario_sabado: '', horario_domingo: '',
  nao_recuperado: false,
  // Outras
  outras_info: '', observacao: '',
};

// Aba "Outros" (22/10/2026): outras empresas do mesmo cliente — os campos que
// aparecem na aba Identificação, menos os do relacionamento com o cliente
// (Consultor, Indicação, Avaliação, Não Recuperado), que valem da ficha principal.
// Ficam na lista outros_cadastros do cliente.
const OUTRO_VAZIO = {
  nome: '', razao_social: '', rua: '', numero: '', bairro: '', cidade: '', estado: '',
  cep: '', telefone: '', referencia: '', outras_info: '', cnpj: '',
  email: '', email_financeiro: '', responsavel: '', po: '', data_contrato: '',
  cpf: '', rg: '', orgao: '', financeiro: '', regime_tributario: '', complemento: '', observacao: '',
};

const TAREFA_NOVA = { descricao: '', data_agendada: '', destinado_a: '', realizado: '', subagenda: 'cliente' };
const SUBAGENDAS_OP = [['', '📥 Geral'], ['administrativo', '🗂️ Administrativo'], ['comercial', '💼 Comercial'], ['cliente', '👥 Cliente'], ['demandas', '⚖️ Demandas Processuais'], ['pessoal', '🏠 Pessoal']];

// "Cadastrado em" (22/10/2026, pedido do Vagner): cliente lançado no sistema
// mostra o dia do lançamento (criado_em). Os que vieram da base antiga foram
// importados em 14/06/2026 — o dia em que o sistema foi montado — sem a data
// de cadastro original; para eles vale a data do 1º depósito de marca.
const DIA_IMPORTACAO = '2026-06-14';
function dataCadastro(c) {
  const criado = c?.criado_em ? new Date(c.criado_em) : null;
  if (!criado || isNaN(criado)) return null;
  const diaBrasilia = criado.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }); // AAAA-MM-DD
  if (diaBrasilia === DIA_IMPORTACAO && !c.origem) {
    const depositos = (c.processos || []).map(p => {
      const s = String(p?.deposito || '');
      const br = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s), iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
      return br ? `${br[3]}-${br[2]}-${br[1]}` : iso ? iso[0] : null;
    }).filter(Boolean).sort();
    if (depositos.length) {
      const [a, m, d] = depositos[0].split('-');
      return { data: `${d}/${m}/${a}`, dica: 'Cliente importado da base antiga: data do 1º depósito de marca' };
    }
    return { data: criado.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }), dica: 'Cliente importado da base antiga, sem data de depósito: dia da importação' };
  }
  return { data: criado.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }), dica: 'Dia em que o cliente foi lançado no sistema' };
}

// Aba Processos (22/10/2026): do mais recente ao mais antigo, pelo nº do processo —
// o INPI numera em ordem de depósito (na base, 362 pares de processos do mesmo
// cliente com datas diferentes: número e data nunca discordaram) e o número existe
// mesmo quando falta a data. Sem número mas com depósito: vale a data. Cartão em
// branco (recém-adicionado): fica no topo.
// Devolve os índices na ordem da tela; a lista gravada não muda.
function ordemRecentes(lista) {
  const num = p => Number(String(p?.numero_processo || '').replace(/\D/g, '')) || 0;
  const dep = p => { const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(String(p?.deposito || '').trim()); return m ? Number(m[3] + m[2] + m[1]) : 0; };
  return lista.map((_, i) => i).sort((a, b) => {
    const na = num(lista[a]), nb = num(lista[b]), da = dep(lista[a]), db = dep(lista[b]);
    if (!na && !da && (nb || db)) return -1;   // em branco no topo
    if (!nb && !db && (na || da)) return 1;
    if (na && nb && na !== nb) return nb - na; // os dois com número
    if (da && db && da !== db) return db - da; // senão, pela data de depósito
    return a - b;
  });
}

function parseDataHora(val) {
  if (!val) return { data: '', hora: '' };
  const [data, hora] = val.includes('T') ? val.split('T') : [val, ''];
  return { data, hora: hora ? hora.slice(0, 5) : '' };
}
function combinarDataHora(data, hora) {
  if (!data) return '';
  return hora ? `${data}T${hora}` : data;
}
function fmtDt(iso) {
  if (!iso) return '—';
  if (iso.includes('T')) {
    const [datePart, timePart] = iso.split('T');
    const [ano, mes, dia] = datePart.split('-');
    return `${dia}/${mes}/${ano.slice(2)} ${timePart.slice(0, 5)}`;
  }
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano.slice(2)}`;
}
function DataHoraInput({ value, onChange, inpStyle }) {
  const { data, hora } = parseDataHora(value);
  function handleHora(e) {
    let v = e.target.value.replace(/[^0-9:]/g, '');
    if (/^\d{4}$/.test(v)) v = v.slice(0, 2) + ':' + v.slice(2);
    onChange(combinarDataHora(data, v));
  }
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input type="date" value={data}
        onChange={e => onChange(combinarDataHora(e.target.value, hora))}
        style={{ ...inpStyle, flex: 1 }} />
      <input type="text" value={hora} placeholder="HH:MM" maxLength={5}
        onChange={handleHora}
        style={{ ...inpStyle, width: 70, flex: 'none' }} />
    </div>
  );
}

const ESTADOS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

function token() { return localStorage.getItem('token'); }
const fmtPrazo = (iso) => iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : '—';
function isAdmin() { return localStorage.getItem('nomeUsuario') === 'Vagner Freitas'; }
// Consultor dono do cliente: mesma comparação do backend (sem caixa, sem espaço)
function ehDonoDoCliente(consultor) {
  const norm = s => String(s || '').trim().toLowerCase();
  return !!norm(consultor) && norm(consultor) === norm(localStorage.getItem('nomeUsuario'));
}
// Estilo base dos inputs
const inp = {
  width: '100%', padding: '3px 6px', borderRadius: 3,
  border: '1px solid #b0b8c4', fontSize: 13, color: '#111',
  boxSizing: 'border-box', background: '#fff', height: 26,
};

const lbl = {
  fontSize: 12, fontWeight: 600, color: '#374151',
  whiteSpace: 'nowrap', paddingRight: 6, minWidth: 0,
};

// Linha de campos: label + input lado a lado
function Row({ fields }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: '1px solid #e8eaed', padding: '3px 0', flexWrap: 'wrap' }}>
      {fields.map(({ label, children, flex = 1, labelFlex }, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div style={{ width: 1, background: '#e8eaed', alignSelf: 'stretch', margin: '0 6px' }} />}
          {/* minWidth min-content: com flex numérico (basis 0) o campo "sempre
              cabia" e o conteúdo vazava no celular — assim ele quebra linha */}
          <div style={{ display: 'flex', alignItems: 'center', flex, minWidth: 'min-content', gap: 4 }}>
            {label && <span style={{ ...lbl, flexShrink: 0, flex: labelFlex || 'none' }}>{label}:</span>}
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function FormCliente({ clienteId: clienteIdProp, dadosIniciais } = {}) {
  const { id: paramId } = useParams();
  const id = clienteIdProp ?? paramId;
  const navigate = useNavigate();
  const location = useLocation();
  const editando = Boolean(id);

  const [aba, setAba] = useState('identificacao');
  const [form, setForm] = useState(() => dadosIniciais ? { ...FORM_VAZIO, ...dadosIniciais } : FORM_VAZIO);
  const [processos, setProcessos] = useState([{ ...PROCESSO_VAZIO }]);
  const [cadastroEm, setCadastroEm] = useState(null); // { data, dica } — fora do form, não volta no salvar
  const [outros, setOutros] = useState([]); // aba Outros: outras empresas do cliente (outros_cadastros)
  // Aba Processos: índices de `processos` na ordem da tela (mais recente no topo).
  // Calculada ao abrir o cliente e mantida enquanto se edita — não pula ao digitar.
  const [ordemProc, setOrdemProc] = useState(null);
  const ordemExibicao = ordemProc && ordemProc.length === processos.length && ordemProc.every(i => i >= 0 && i < processos.length)
    ? ordemProc : ordemRecentes(processos);
  // Empresa da aba Outros a que o processo pertence (null = cadastro principal)
  const empresaDoProcesso = p => (p?.cadastro_id ? outros.find(o => o.id === p.cadastro_id) || null : null);

  // ⚖️ Demandas processuais do cliente (lançadas por processo)
  const [demandasCliente, setDemandasCliente] = useState([]);
  const [modalNovaDemanda, setModalNovaDemanda] = useState(null); // clienteInfo
  const [demandaEditar, setDemandaEditar] = useState(null);
  const [desfechoDemanda, setDesfechoDemanda] = useState(null); // {demanda, acao}
  const carregarDemandas = useCallback(() => {
    if (!id) return;
    fetch(`/demandas?cliente_id=${id}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => Array.isArray(d) && setDemandasCliente(d)).catch(() => {});
  }, [id]);
  useEffect(() => { carregarDemandas(); }, [carregarDemandas]);
  async function reabrirDemanda(dm) {
    await fetch(`/demandas/${dm._id}/reabrir`, { method: 'PUT', headers: { Authorization: `Bearer ${token()}` } }).catch(() => {});
    carregarDemandas();
  }
  async function excluirDemanda(dm) {
    if (!window.confirm(`Excluir a demanda "${dm.tipo}"?`)) return;
    const r = await fetch(`/demandas/${dm._id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }).then(x => x.json()).catch(() => ({}));
    if (r.erro) window.alert(r.erro);
    carregarDemandas();
  }
  const [carregando, setCarregando] = useState(editando);
  const [erro, setErro] = useState('');
  const [confirmacaoProcesso, setConfirmacaoProcesso] = useState(null);
  const [consultando, setConsultando] = useState(null);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroCep, setErroCep] = useState('');
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [erroCnpj, setErroCnpj] = useState('');

  const [listaNavegacao, setListaNavegacao] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [novaTarefa, setNovaTarefa] = useState({ ...TAREFA_NOVA, destinado_a: localStorage.getItem('nomeUsuario') || '' });
  const [adicionandoTarefa, setAdicionandoTarefa] = useState(true);
  const [salvandoTarefa, setSalvandoTarefa] = useState(false);
  const [editandoTarefa, setEditandoTarefa] = useState(null);
  const [tarefaEditForm, setTarefaEditForm] = useState({});
  const [confirmacaoTarefa, setConfirmacaoTarefa] = useState(null);

  // Carrega lista ordenada de clientes para navegação
  useEffect(() => {
    api.listarClientes()
      .then(lista => {
        const comData = lista.filter(c => c.criado_em).sort((a, b) => new Date(a.criado_em) - new Date(b.criado_em));
        const semData = lista.filter(c => !c.criado_em).sort((a, b) => {
          const na = Number((a.processos?.[0]?.numero_processo || a.numero_processo || '0').replace(/\D/g,''));
          const nb = Number((b.processos?.[0]?.numero_processo || b.numero_processo || '0').replace(/\D/g,''));
          return nb - na;
        });
        setListaNavegacao([...comData, ...semData].map(c => c._id));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editando) return;
    let ativo = true; // trocou de cliente antes da resposta (‹ › clicadas rápido): descarta
    api.buscarCliente(id)
      .then(c => {
        if (!ativo) return;
        setForm({
          nome: c.nome || '', razao_social: c.razao_social || '',
          cpf: c.cpf || '', rg: c.rg || '', orgao: c.orgao || '',
          cnpj: c.cnpj || '', data_nascimento: c.data_nascimento || '',
          data_contrato: c.data_contrato || '',
          inscricao_municipal: c.inscricao_municipal || '',
          inscricao_estadual: c.inscricao_estadual || '',
          telefone: c.telefone || '', email: c.email || '',
          email_financeiro: c.email_financeiro || '',
          responsavel: c.responsavel || '', po: c.po || '',
          cep: c.cep || '', rua: c.rua || '', numero: c.numero || '',
          complemento: c.complemento || '', bairro: c.bairro || '',
          cidade: c.cidade || '', estado: c.estado || '',
          referencia: c.referencia || '',
          shopping: c.shopping || '', contador: c.contador || '',
          programa: c.programa || '', atividade: c.atividade || '',
          tipo_pagamento: c.tipo_pagamento || '',
          consultor: c.consultor || '',
          indicacao: c.indicacao || '', avaliacao: c.avaliacao || '',
          financeiro: c.financeiro || '', regime_tributario: c.regime_tributario || '',
          horario_seg_sex: c.horario_seg_sex || '',
          horario_sabado: c.horario_sabado || '',
          horario_domingo: c.horario_domingo || '',
          nao_recuperado: c.nao_recuperado || false,
          outras_info: c.outras_info || '', observacao: c.observacao || '',
        });
        const procs = c.processos?.length
          ? c.processos.map(p => ({ ...PROCESSO_VAZIO, ...p }))
          : [{ ...PROCESSO_VAZIO, nome_marca: c.nome_marca || '', numero_processo: c.numero_processo || '' }];
        setProcessos(procs);
        setOrdemProc(ordemRecentes(procs));
        setCadastroEm(dataCadastro(c));
        setOutros(Array.isArray(c.outros_cadastros) ? c.outros_cadastros : []);
        if (c.venda) setVenda(c.venda);
        if (c.parcelas?.length) setParcelas(c.parcelas);
        // A tela passa a ser deste cliente: o salvamento automático recomeça do zero
        idNaTelaRef.current = id;
        ultimoSalvoRef.current = null;
        setAutoStatus('');
        setErro('');
      })
      .catch(e => ativo && setErro(e.message))
      .finally(() => ativo && setCarregando(false));
    return () => { ativo = false; };
  }, [id, editando]);

  const carregarTarefas = useCallback(() => {
    if (!id) return;
    fetch(`/agenda?cliente_id=${id}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(data => setTarefas([...data].sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em))))
      .catch(() => {});
  }, [id]);

  useEffect(() => { carregarTarefas(); }, [carregarTarefas]);

  function f(name) { return form[name] ?? ''; }

  // Navegação entre clientes
  const idxAtual = listaNavegacao.indexOf(id);
  // Quando não há id (cadastro em branco/novo), ‹ vai para o último da lista
  const idAnterior = idxAtual > 0
    ? listaNavegacao[idxAtual - 1]
    : idxAtual === -1 && listaNavegacao.length > 0
      ? listaNavegacao[listaNavegacao.length - 1]
      : null;
  const idProximo = idxAtual >= 0 && idxAtual < listaNavegacao.length - 1 ? listaNavegacao[idxAtual + 1] : null;
  const cadastroEmBranco = !form.nome && !form.razao_social && !form.responsavel &&
    processos.every(p => !p.nome_marca && !p.numero_processo);

  function navegar(destId) { navigate(`/clientes/${destId}/editar`); }

  async function buscarCep(cepVal) {
    const c = (cepVal || form.cep || '').replace(/\D/g, '');
    if (c.length !== 8) { if (cepVal === undefined) setErroCep('CEP deve ter 8 dígitos.'); return; }
    const doCliente = idNaTelaRef.current;
    setErroCep('');
    setBuscandoCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const data = await res.json();
      if (!mesmoCliente(doCliente)) return; // trocou de cliente enquanto consultava
      if (data.erro) { setErroCep('CEP não encontrado.'); }
      else {
        setForm(prev => ({
          ...prev,
          rua: data.logradouro || prev.rua,
          bairro: data.bairro || prev.bairro,
          cidade: data.localidade || prev.cidade,
          estado: data.uf || prev.estado,
        }));
      }
    } catch { setErroCep('Erro ao consultar o CEP.'); }
    finally { setBuscandoCep(false); }
  }

  async function buscarCnpj() {
    const cnpj = form.cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) { setErroCnpj('CNPJ deve ter 14 dígitos.'); return; }
    const doCliente = idNaTelaRef.current;
    setErroCnpj('');
    setBuscandoCnpj(true);
    try {
      const res = await fetch(`/clientes/consultar-cnpj/${cnpj}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) { const e = await res.json(); setErroCnpj(e.erro || 'CNPJ não encontrado na Receita Federal.'); return; }
      const d = await res.json();
      if (!mesmoCliente(doCliente)) return; // trocou de cliente enquanto consultava
      setForm(prev => ({
        ...prev,
        razao_social: d.razao_social || prev.razao_social,
        nome:         d.nome_fantasia || prev.nome,
        email:        d.email || prev.email,
        telefone:     d.telefone ? d.telefone.replace(/\D/g,'').replace(/^(\d{2})(\d+)/, '($1) $2') : prev.telefone,
        cep:          d.cep || prev.cep,
        rua:          d.rua || prev.rua,
        numero:       d.numero || prev.numero,
        complemento:  d.complemento || prev.complemento,
        bairro:       d.bairro || prev.bairro,
        cidade:       d.cidade || prev.cidade,
        estado:       d.estado || prev.estado,
        atividade:    d.atividade || prev.atividade,
      }));
    } catch { setErroCnpj('Erro ao consultar a Receita Federal.'); }
    finally { setBuscandoCnpj(false); }
  }

  function atualizarProcesso(index, field, value) {
    setProcessos(p => p.map((item, i) => i === index ? { ...item, [field]: value } : item));
  }

  // O processo novo entra no fim da lista gravada, mas aparece no TOPO da tela
  function adicionarProcesso() {
    const novo = processos.length;
    setProcessos(p => [...p, { ...PROCESSO_VAZIO }]);
    setOrdemProc([novo, ...ordemExibicao]);
  }

  function confirmarRemocaoProcesso() {
    const r = confirmacaoProcesso;
    setProcessos(p => p.filter((_, i) => i !== r));
    setOrdemProc(ordemExibicao.filter(i => i !== r).map(i => (i > r ? i - 1 : i)));
    setConfirmacaoProcesso(null);
  }

  // 🏛️ Baixa a página oficial do processo no site do INPI em PDF (~20-30s)
  const [baixandoPagInpi, setBaixandoPagInpi] = useState(null);
  async function baixarPaginaInpi(index) {
    const numero = (processos[index].numero_processo || '').replace(/\D/g, '');
    if (!numero) return alert('Preencha o número do processo antes de baixar a página.');
    if (baixandoPagInpi !== null) return;
    setBaixandoPagInpi(index);
    try {
      const r = await fetch(`/inpi/pagina-pdf/${encodeURIComponent(numero)}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) {
        let det = ''; try { det = (await r.json()).erro || ''; } catch { /* não-JSON */ }
        return alert(`Não consegui baixar a página do INPI (HTTP ${r.status}). ${det}`.trim());
      }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      const nomeLimpo = String(processos[index].nome_marca || '').trim().replace(/[\\/:*?"<>|]/g, '');
      a.href = url; a.download = nomeLimpo ? `${nomeLimpo}.pdf` : `INPI ${numero}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Falha ao baixar a página do INPI: ' + (e?.message || 'erro de rede'));
    } finally {
      setBaixandoPagInpi(null);
    }
  }

  async function consultarINPI(index) {
    const numero = processos[index].numero_processo?.trim();
    if (!numero) return alert('Preencha o número do processo antes de consultar.');
    const doCliente = idNaTelaRef.current;
    setConsultando(index);
    try {
      const res = await fetch(`/inpi/consultar/${encodeURIComponent(numero)}`, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      if (!mesmoCliente(doCliente)) return; // trocou de cliente enquanto consultava
      if (!res.ok) throw new Error(data.erro || 'Erro ao consultar');
      if (!data.encontrado) return alert(data.mensagem || 'Processo não encontrado.');
      const p = data.processo;
      setProcessos(prev => prev.map((item, i) => i === index ? {
        ...item,
        nome_marca: p.marca || item.nome_marca, situacao: p.situacao || item.situacao,
        apresentacao: p.apresentacao || item.apresentacao, natureza: p.natureza || item.natureza,
        classes: p.classes || item.classes, deposito: p.deposito || item.deposito,
        concessao: p.concessao || item.concessao, vencimento: p.vencimento || item.vencimento,
        titular: p.titular || item.titular, procurador: p.procurador || item.procurador,
      } : item));
    } catch (e) {
      const msg = e.message?.includes('timeout') || e.message?.includes('INPI_INACESSIVEL')
        ? 'O INPI está inacessível no momento.\n\nConsulte manualmente em:\nhttps://busca.inpi.gov.br'
        : 'Erro ao consultar INPI: ' + e.message;
      alert(msg);
    } finally {
      setConsultando(null);
    }
  }

  // 🔔 Lembrete no WhatsApp 30min antes: usa o mesmo motor da Agenda
  // (campo lembrete_em + robô que varre a cada 5 min e dispara pro
  // telefone de quem marcou). Precisa de HORA na tarefa pra ter de onde
  // descontar os 30 minutos.
  const MIN_ANTES = 30;
  async function aplicarLembrete(tid, dataAgendada, querLembrete) {
    if (!tid) return '';
    if (!querLembrete) {
      await fetch(`/agenda/${tid}/lembrete`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }).catch(() => {});
      return '';
    }
    if (!String(dataAgendada || '').includes('T')) {
      return '⚠️ A tarefa foi salva, mas o lembrete precisa de HORA — preencha o campo HH:MM e marque de novo.';
    }
    const quando = new Date(new Date(dataAgendada).getTime() - MIN_ANTES * 60000);
    const p = (n) => String(n).padStart(2, '0');
    const iso = `${quando.getFullYear()}-${p(quando.getMonth() + 1)}-${p(quando.getDate())}T${p(quando.getHours())}:${p(quando.getMinutes())}`;
    const r = await fetch(`/agenda/${tid}/lembrete`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ lembrete_em: iso }),
    }).catch(() => null);
    if (!r || !r.ok) return '⚠️ Tarefa salva, mas não consegui marcar o lembrete — tente pelo botão 🔔 da Agenda.';
    return `🔔 Lembrete marcado: você recebe no WhatsApp às ${p(quando.getHours())}:${p(quando.getMinutes())} (30 min antes).`;
  }

  async function salvarTarefa() {
    if (!novaTarefa.descricao.trim()) return alert('Preencha a descrição da tarefa.');
    setSalvandoTarefa(true);
    const nomeCliente = form.nome || processos[0]?.nome_marca || '';
    const r = await fetch('/agenda', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ ...novaTarefa, cliente_id: id, cliente_nome: nomeCliente }),
    });
    const criada = await r.json().catch(() => null);
    const aviso = await aplicarLembrete(criada?._id, novaTarefa.data_agendada, novaTarefa.lembrar30);
    setSalvandoTarefa(false);
    if (aviso) alert(aviso);
    setNovaTarefa({ ...TAREFA_NOVA, destinado_a: localStorage.getItem('nomeUsuario') || '' });
    setAdicionandoTarefa(false);
    carregarTarefas();
  }

  async function salvarEdicaoTarefa(tid) {
    await fetch(`/agenda/${tid}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify(tarefaEditForm),
    });
    const aviso = await aplicarLembrete(tid, tarefaEditForm.data_agendada, tarefaEditForm.lembrar30);
    if (aviso) alert(aviso);
    setEditandoTarefa(null);
    carregarTarefas();
  }

  async function excluirTarefa() {
    await fetch(`/agenda/${confirmacaoTarefa}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
    setConfirmacaoTarefa(null);
    carregarTarefas();
  }

  // ── Estado da aba Venda ──────────────────────────────────────────────────
  const VENDA_VAZIA = {
    servico: '', valor: '', forma_pagamento: '', num_parcelas: 1, data_primeira_parcela: '',
  };
  const [venda, setVenda] = useState(VENDA_VAZIA);
  const [parcelas, setParcelas] = useState([]);


  const FORMAS_PARCELADAS = ['Cartão de Crédito', 'Boleto Parcelado', 'Pix Parcelado'];
  const isParcelado = FORMAS_PARCELADAS.includes(venda.forma_pagamento);

  useEffect(() => {
    if (!isParcelado || !venda.valor || !venda.num_parcelas || !venda.data_primeira_parcela) {
      setParcelas([]);
      return;
    }
    const total = parseFloat(venda.valor.replace(',', '.')) || 0;
    const n = parseInt(venda.num_parcelas) || 1;
    const valorParcela = total / n;
    const base = new Date(venda.data_primeira_parcela + 'T12:00:00');
    const novas = Array.from({ length: n }, (_, i) => {
      const dt = new Date(base);
      dt.setMonth(dt.getMonth() + i);
      return {
        numero: i + 1,
        vencimento: dt.toLocaleDateString('pt-BR'),
        valor: valorParcela.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        status: 'Pendente',
      };
    });
    setParcelas(novas);
  }, [venda.valor, venda.forma_pagamento, venda.num_parcelas, venda.data_primeira_parcela, isParcelado]);

  // ── Salvamento automático ────────────────────────────────────────────────
  // Salva 1s após a última alteração em qualquer campo, em qualquer aba.
  // Cliente novo: o primeiro salvamento cria o registro e entra em modo edição.
  const [autoStatus, setAutoStatus] = useState(''); // '' | 'salvando' | 'salvo' | 'erro'
  const [msgCliente, setMsgCliente] = useState(false); // 💬 agendar mensagem pro cliente
  // 📋 Especificações de Nice do processo (modal com copiar tudo)
  const [especif, setEspecif] = useState(null);
  const [especifCopiado, setEspecifCopiado] = useState(false);

  async function abrirEspecificacoes(p) {
    const num = String(p.numero_processo || '').replace(/\D/g, '');
    if (num.length < 6) return alert('Este processo não tem número válido.');
    try {
      const r = await fetch(`/marcas-rpi/especificacoes/${num}`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro || 'Falha na consulta');
      setEspecifCopiado(false);
      setEspecif({ ...d, marca: p.nome_marca || d.nome_marca });
    } catch (e) { alert('Não consegui buscar as especificações: ' + e.message); }
  }

  function copiarEspecificacoes() {
    if (!especif) return;
    const texto = (especif.classes || [])
      .map(c => `Classe ${c.codigo}${c.status ? ` (${c.status})` : ''}\n${c.especificacao || '(sem especificação)'}`)
      .join('\n\n');
    navigator.clipboard.writeText(`${especif.marca} — processo ${especif.numero}\n\n${texto}`)
      .then(() => { setEspecifCopiado(true); setTimeout(() => setEspecifCopiado(false), 2500); })
      .catch(() => alert('Não consegui copiar — selecione e copie manualmente.'));
  }
  const ultimoSalvoRef = useRef(null);
  const criandoRef = useRef(false);
  // Troca de cliente com a ficha aberta (setas ‹ ›, busca do topo — 22/10/2026):
  // a URL muda na hora, mas os dados do novo cliente chegam um instante depois.
  const idAbertoRef = useRef(id);     // cliente aberto agora (null ao sair da ficha)
  const idNaTelaRef = useRef(editando ? null : id); // de qual cliente são os dados que estão na tela
  const pendenteRef = useRef(null);   // { id, dados, nome }: alteração esperando o 1 s para gravar
  // CEP / CNPJ / Consultar INPI: a resposta só entra se a tela ainda for do mesmo cliente
  const mesmoCliente = doCliente => idNaTelaRef.current === doCliente && idAbertoRef.current === doCliente;
  // Cadastro do cliente: só o Vagner altera (pedido dele, 01/10/2026). Para os
  // demais a ficha abre só para leitura e a gravação automática não roda — o
  // servidor também recusa (PUT /clientes/:id → 403).
  const somenteLeitura = editando && !isAdmin();

  useEffect(() => {
    if (carregando || somenteLeitura) return;
    // Trocou de cliente e os dados do novo ainda não chegaram: a tela ainda mostra
    // o anterior, então não compara nem grava nada.
    if (editando && idNaTelaRef.current !== id) return;
    const dados = {
      ...form, processos,
      nome_marca: processos[0]?.nome_marca || '',
      numero_processo: processos[0]?.numero_processo || '',
      venda, parcelas,
      outros_cadastros: outros,
    };
    const snapshot = JSON.stringify(dados);
    // Primeira execução após carregar: só registra o estado, não salva.
    if (ultimoSalvoRef.current === null) { ultimoSalvoRef.current = snapshot; return; }
    if (snapshot === ultimoSalvoRef.current) return;

    const nome = form.nome || form.razao_social || 'um cliente';
    if (editando) pendenteRef.current = { id, dados, nome };
    const timer = setTimeout(async () => {
      // Cliente novo só é criado quando há pelo menos um nome.
      if (!editando && !form.nome.trim() && !form.razao_social.trim()) return;
      if (!editando && criandoRef.current) return;
      pendenteRef.current = null;
      setAutoStatus('salvando');
      setErro('');
      try {
        if (editando) {
          await api.atualizarCliente(id, dados);
        } else {
          criandoRef.current = true;
          const novo = await api.criarCliente(dados);
          ultimoSalvoRef.current = snapshot;
          setAutoStatus('salvo');
          navigate(`/clientes/${novo._id}/editar`, { replace: true });
          return;
        }
        if (idAbertoRef.current !== id) return; // gravou, mas a tela já é de outro cliente
        ultimoSalvoRef.current = snapshot;
        setAutoStatus('salvo');
        setTimeout(() => setAutoStatus(s => (s === 'salvo' ? '' : s)), 2500);
      } catch (e) {
        if (editando && idAbertoRef.current !== id) {
          alert(`Não consegui salvar a última alteração de ${nome}: ${e.message}`);
          return;
        }
        setAutoStatus('erro');
        setErro('Falha ao salvar automaticamente: ' + e.message);
        if (!editando) criandoRef.current = false;
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [form, processos, venda, parcelas, outros, carregando, editando, id, navigate, somenteLeitura]);

  // Saiu do cliente (‹ ›, busca do topo ou outra tela) antes do 1 s: grava na
  // hora a última alteração — no cliente em que ela foi feita.
  useEffect(() => {
    idAbertoRef.current = id;
    return () => {
      idAbertoRef.current = null;
      const p = pendenteRef.current;
      if (!p) return;
      pendenteRef.current = null;
      api.atualizarCliente(p.id, p.dados)
        .catch(e => alert(`Não consegui salvar a última alteração de ${p.nome}: ${e.message}`));
    };
  }, [id]);

  if (carregando) return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 10, gap: 6 }}>
        <button type="button" disabled style={{ background: '#e5e7eb', color: '#9ca3af', border: '1px solid #d1d5db', borderRadius: 6, width: 36, height: 36, fontSize: 22, cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
        <button type="button" disabled style={{ background: '#e5e7eb', color: '#9ca3af', border: '1px solid #d1d5db', borderRadius: 6, width: 36, height: 36, fontSize: 22, cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
      </div>
      <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>Carregando...</div>
    </div>
  );

  const ABAS = [
    { key: 'identificacao', label: 'Identificação' },
    { key: 'outros',        label: outros.length ? `Outros (${outros.length})` : 'Outros' },
    { key: 'processos',     label: 'Processos' },
    { key: 'venda',         label: 'Venda' },
    { key: 'oportunidades', label: 'Oportunidade' },
    { key: 'documentos',    label: 'Documentos' },
    { key: 'tarefas',       label: 'Tarefas' },
    { key: 'mensagens',     label: '💬 Mensagens' },
    { key: 'upload',        label: 'Upload' },
  ];

  return (
    <div style={{ maxWidth: 960 }}>
      {confirmacaoProcesso !== null && (
        <ModalConfirmar mensagem="Remover este processo?" onConfirmar={confirmarRemocaoProcesso} onCancelar={() => setConfirmacaoProcesso(null)} />
      )}
      {/* 📋 Especificações de Nice */}
      {especif && (
        <div onClick={() => setEspecif(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 640, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ background: '#0a1628', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>📋 Especificações de Nice</div>
                <div style={{ color: '#94a3b8', fontSize: 11.5 }}>{especif.marca} · processo {especif.numero}</div>
              </div>
              <button onClick={() => setEspecif(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {(especif.classes || []).length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>A biblioteca não tem as classes deste processo.</div>}
              {(especif.classes || []).map((c, k) => (
                <div key={k} style={{ marginBottom: 12, border: '1px solid #e8eaed', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ background: '#f8fafc', padding: '6px 12px', fontSize: 12.5, fontWeight: 800, color: '#0a1628' }}>
                    Classe {c.codigo}{c.status ? <span style={{ color: '#6b7280', fontWeight: 600 }}> · {c.status}</span> : null}
                  </div>
                  <div style={{ padding: '8px 12px', fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {c.especificacao || '(sem especificação registrada)'}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #e8eaed', padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setEspecif(null)} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>Fechar</button>
              <button onClick={copiarEspecificacoes}
                style={{ background: especifCopiado ? '#16a34a' : '#0a1628', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {especifCopiado ? '✓ Copiado!' : '📋 Copiar tudo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmacaoTarefa && (
        <ModalConfirmar mensagem="Excluir esta tarefa?" onConfirmar={excluirTarefa} onCancelar={() => setConfirmacaoTarefa(null)} />
      )}

      {/* Cabeçalho */}
      {/* ⚖️ Modais das demandas processuais */}
      {modalNovaDemanda && <ModalDemanda clienteInfo={modalNovaDemanda}
        onFechar={() => setModalNovaDemanda(null)} onSalvo={() => { setModalNovaDemanda(null); carregarDemandas(); }} />}
      {demandaEditar && <ModalDemanda demanda={demandaEditar}
        onFechar={() => setDemandaEditar(null)} onSalvo={() => { setDemandaEditar(null); carregarDemandas(); }} />}
      {desfechoDemanda && <ModalDesfechoDemanda demanda={desfechoDemanda.demanda} acao={desfechoDemanda.acao}
        onFechar={() => setDesfechoDemanda(null)} onSalvo={() => { setDesfechoDemanda(null); carregarDemandas(); }} />}

      {msgCliente && <AgendarMensagemCliente
        alvo={{ cliente_id: paramId || '', cliente_nome: f('nome') || '', telefone: f('telefone') || '' }}
        onFechar={() => setMsgCliente(false)} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <Link to="/clientes" style={{ color: '#6b7280', fontSize: 12, textDecoration: 'none' }}>← Clientes</Link>
          <h1 style={{ fontSize: 17, fontWeight: 700, marginTop: 2, color: '#0a1628' }}>
            {/* Título vivo (pedido 17/08): primeiro nome do cliente + marca —
                navegando com os botões ‹ › dá pra achar o cliente sem abrir nada */}
            {(() => {
              if (!editando) return 'Novo Cliente';
              const primeiro = (form.nome || form.razao_social || '').trim().split(/\s+/)[0] || '';
              const marca = (processos[0]?.nome_marca || form.nome_marca || '').trim();
              const titulo = [primeiro, marca].filter(Boolean).join(' · ');
              return titulo || 'Editar Cliente';
            })()}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {editando && (
              <button type="button" onClick={() => setMsgCliente(true)}
                title="Agendar uma mensagem de WhatsApp para este cliente, na data e hora que você escolher"
                style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 4, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                💬 Agendar mensagem
              </button>
            )}
            {autoStatus === 'salvando' && <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>Salvando...</span>}
            {autoStatus === 'salvo' && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ Salvo</span>}
            {autoStatus === 'erro' && <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>⚠ Erro ao salvar</span>}
            {listaNavegacao.length > 0 && idxAtual >= 0 && (
              <span style={{ fontSize: 11, color: '#9ca3af' }}>{idxAtual + 1} / {listaNavegacao.length}</span>
            )}
            <button
              type="button"
              onClick={() => idAnterior && navegar(idAnterior)}
              disabled={!idAnterior}
              title="Cliente anterior"
              style={{ background: idAnterior ? '#0a1628' : '#e5e7eb', color: idAnterior ? '#fff' : '#9ca3af', border: '1px solid #d1d5db', borderRadius: 6, width: 36, height: 36, fontSize: 22, cursor: idAnterior ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0 }}>
              ‹
            </button>
            <button
              type="button"
              onClick={() => idProximo && !cadastroEmBranco && navegar(idProximo)}
              disabled={!idProximo || cadastroEmBranco}
              title={cadastroEmBranco ? 'Cadastro em branco — próximo bloqueado' : 'Próximo cliente'}
              style={{ background: (idProximo && !cadastroEmBranco) ? '#0a1628' : '#e5e7eb', color: (idProximo && !cadastroEmBranco) ? '#fff' : '#9ca3af', border: '1px solid #d1d5db', borderRadius: 6, width: 36, height: 36, fontSize: 22, cursor: (idProximo && !cadastroEmBranco) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0 }}>
              ›
            </button>
          </div>
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', borderBottom: '2px solid #0a1628', marginBottom: 0, flexWrap: 'wrap' }}>
        {ABAS.map(a => (
          <button key={a.key} type="button" onClick={() => setAba(a.key)}
            style={{
              padding: '6px 18px', fontSize: 13, fontWeight: 600,
              color: aba === a.key ? '#fff' : '#374151',
              background: aba === a.key ? '#0a1628' : '#e5e7eb',
              border: 'none', cursor: 'pointer', borderRadius: '6px 6px 0 0',
              marginRight: 2,
            }}>
            {a.label}
          </button>
        ))}
      </div>

      <form onSubmit={e => e.preventDefault()}>

        {/* ── ABA: OPORTUNIDADE (negócios da carteira, presos a esta ficha) ── */}
        {aba === 'oportunidades' && (
          <OportunidadesCliente
            clienteId={editando ? paramId : ''}
            nome={form.nome || form.razao_social || ''}
            telefone={form.telefone || ''}
            consultor={form.consultor || ''}
          />
        )}

        {/* ── ABA: IDENTIFICAÇÃO ── */}
        {aba === 'identificacao' && (
          <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '10px 14px' }}>
          <fieldset disabled={somenteLeitura} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

            <CamposIdentificacao
              valor={f}
              alterar={(campo, v) => setForm(prev => ({ ...prev, [campo]: v }))}
              cadastradoEm={editando ? (cadastroEm?.data || '—') : new Date().toLocaleDateString('pt-BR')}
              cadastradoDica={cadastroEm?.dica}
              cep={{ buscar: buscarCep, buscando: buscandoCep, erro: erroCep }}
              cnpj={{ buscar: buscarCnpj, buscando: buscandoCnpj, erro: erroCnpj }}
            />

          </fieldset>
            {erro && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{erro}</div>}

          </div>
        )}

        {/* ── ABA: OUTROS — outras empresas do mesmo cliente ── */}
        {aba === 'outros' && (
          <AbaOutros lista={outros} alterarLista={setOutros} somenteLeitura={somenteLeitura}
            nomeCliente={form.nome || form.razao_social || ''}
            contarProcessos={idEmp => processos.filter(p => p.cadastro_id === idEmp).length}
            aoExcluir={idEmp => setProcessos(ps => ps.map(p => (p.cadastro_id === idEmp ? { ...p, cadastro_id: '' } : p)))} />
        )}

        {/* ── ABA: PROCESSOS ── */}
        {aba === 'processos' && (
          <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '10px 14px' }}>
            {!somenteLeitura && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <BtnPrimary type="button" onClick={adicionarProcesso}>+ Adicionar Processo</BtnPrimary>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {ordemExibicao.map(i => [processos[i], i]).map(([p, i]) => (
                <div key={i} style={{ border: '1px solid #c8d0da', borderRadius: 4, overflow: 'hidden' }}>
                  {/* Header processo */}
                  <div style={{ background: '#0a1628', padding: '5px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                      {p.nome_marca ? `™ ${p.nome_marca}` : p.numero_processo ? `Processo ${p.numero_processo}` : processoVazio(p) ? 'Novo processo' : 'Processo sem número'}
                      {empresaDoProcesso(p) && (
                        <span title="Este processo é de uma empresa da aba Outros"
                          style={{ marginLeft: 8, background: '#1d4ed8', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>
                          🏢 {empresaDoProcesso(p).nome || empresaDoProcesso(p).razao_social || 'outra empresa'}
                        </span>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button"
                        onClick={() => id
                          ? setModalNovaDemanda({ cliente_id: id, cliente_nome: form.nome || form.razao_social || '', processo_numero: (p.numero_processo || '').trim(), marca: p.nome_marca || '' })
                          : window.alert('Salve o cliente antes de lançar demandas.')}
                        title="Lançar demanda processual (oposição, recurso, exigência…) com prazo"
                        style={{ background: '#7c3aed', color: '#fff', padding: '3px 10px', borderRadius: 3, fontSize: 12, border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        ⚖️ + Demanda
                      </button>
                      <button type="button" onClick={() => baixarPaginaInpi(i)} disabled={baixandoPagInpi !== null}
                        title="Baixa a página oficial deste processo no site do INPI em PDF (demora ~30s)"
                        style={{ background: '#0891b2', color: '#fff', padding: '3px 10px', borderRadius: 3, fontSize: 12, border: 'none', cursor: baixandoPagInpi !== null ? 'wait' : 'pointer', fontWeight: 600, opacity: baixandoPagInpi !== null && baixandoPagInpi !== i ? 0.6 : 1 }}>
                        {baixandoPagInpi === i ? '⏳ Baixando…' : '🏛️ Página INPI'}
                      </button>
                      {!somenteLeitura && (
                        <button type="button" onClick={() => consultarINPI(i)} disabled={consultando === i}
                          style={{ background: '#fff', color: '#0a1628', padding: '3px 10px', borderRadius: 3, fontSize: 12, border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                          {consultando === i ? '⏳ ...' : '🌐 Consultar INPI'}
                        </button>
                      )}
                      {/* Remover processo: só o Vagner vê o botão (01/10/2026) */}
                      {!somenteLeitura && processos.length > 1 && (
                        <button type="button" onClick={() => setConfirmacaoProcesso(i)}
                          style={{ background: '#dc2626', color: '#fff', padding: '3px 10px', borderRadius: 3, fontSize: 12, border: 'none', cursor: 'pointer' }}>
                          × Remover
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Campos do processo em linhas densas */}
                  <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column' }}>
                    {/* De qual cadastro é o processo — só aparece quando há empresas na aba Outros */}
                    {outros.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid #e8eaed', padding: '3px 0' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', paddingRight: 4 }}>Cadastro:</span>
                        <select value={p.cadastro_id || ''} disabled={somenteLeitura} onChange={e => atualizarProcesso(i, 'cadastro_id', e.target.value)}
                          title="De qual cadastro é este processo: o principal (aba Identificação) ou uma empresa da aba Outros"
                          style={{ flex: 1, padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', height: 26, background: somenteLeitura ? '#f5f6f8' : '#fff' }}>
                          <option value="">Principal — {form.nome || form.razao_social || 'cadastro da aba Identificação'}</option>
                          {outros.map(o => (
                            <option key={o.id} value={o.id}>🏢 {o.nome || o.razao_social || '(sem nome)'}{(o.cnpj || o.cpf) ? ` — ${o.cnpj || o.cpf}` : ''}</option>
                          ))}
                          {p.cadastro_id && !empresaDoProcesso(p) && <option value={p.cadastro_id}>(empresa removida da aba Outros)</option>}
                        </select>
                      </div>
                    )}
                    <PRow leitura={somenteLeitura} label="Nº Processo" value={p.numero_processo} onChange={v => atualizarProcesso(i, 'numero_processo', v)} placeholder="Ex: 935613161"
                      extra={[
                        { label: 'Nome da Marca', value: p.nome_marca, onChange: v => atualizarProcesso(i, 'nome_marca', v), placeholder: 'Nome da marca' },
                      ]} />
                    <PRow leitura={somenteLeitura} label="Situação" value={p.situacao} onChange={v => atualizarProcesso(i, 'situacao', v)} placeholder="Ex: Registro de marca em vigor" full />
                    <PRow leitura={somenteLeitura} label="Titular" value={p.titular} onChange={v => atualizarProcesso(i, 'titular', v)}
                      extra={[{ label: 'Procurador', value: p.procurador, onChange: v => atualizarProcesso(i, 'procurador', v) }]} />
                    <PRow leitura={somenteLeitura} label="Apresentação" value={p.apresentacao} onChange={v => atualizarProcesso(i, 'apresentacao', v)}
                      extra={[{ label: 'Natureza', value: p.natureza, onChange: v => atualizarProcesso(i, 'natureza', v) }]} />
                    <PRow leitura={somenteLeitura} label="Classe de Nice" value={p.classes} onChange={v => atualizarProcesso(i, 'classes', v)} placeholder="Ex: NCL(12) 45"
                      acao={{ rotulo: '📋 Especificações', title: 'Ver e copiar as especificações completas das classes (da biblioteca de RPIs)', onClick: () => abrirEspecificacoes(p) }}
                      extra={[
                        { label: 'Depósito', value: p.deposito, onChange: v => atualizarProcesso(i, 'deposito', v), placeholder: 'dd/mm/aaaa' },
                        { label: 'Concessão', value: p.concessao, onChange: v => atualizarProcesso(i, 'concessao', v), placeholder: 'dd/mm/aaaa' },
                        { label: 'Vigência', value: p.vencimento, onChange: v => atualizarProcesso(i, 'vencimento', v), placeholder: 'dd/mm/aaaa' },
                      ]} />

                    {/* ⚖️ Demandas deste processo (casadas pelo nº) */}
                    {(() => {
                      const digitos = (p.numero_processo || '').replace(/\D/g, '');
                      const doProcesso = digitos ? demandasCliente.filter(dm => (dm.processo_numero || '').replace(/\D/g, '') === digitos) : [];
                      if (!doProcesso.length) return null;
                      return (
                        <div style={{ marginTop: 6, borderTop: '1px dashed #e5e7eb', paddingTop: 6 }}>
                          {doProcesso.map(dm => {
                            const u = urgencia(dm);
                            return (
                              <div key={dm._id} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '3px 0', fontSize: 12 }}>
                                <span style={{ background: u.bg, color: u.cor, padding: '1px 7px', borderRadius: 3, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{u.rotulo}</span>
                                <span style={{ fontWeight: 700, color: '#0a1628' }}>⚖️ {dm.tipo}</span>
                                <span style={{ color: '#6b7280' }}>prazo {fmtPrazo(dm.prazo)}</span>
                                {dm.observacao && <span style={{ color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>· {dm.observacao}</span>}
                                <span style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                                  {dm.status === 'pendente' ? (
                                    <>
                                      <button type="button" onClick={() => setDesfechoDemanda({ demanda: dm, acao: 'cumprir' })}
                                        style={{ background: '#16a34a', color: '#fff', padding: '1px 8px', borderRadius: 3, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer' }}>✓</button>
                                      <button type="button" onClick={() => setDesfechoDemanda({ demanda: dm, acao: 'nao' })} title="Não cumprida"
                                        style={{ background: '#fff', color: '#dc2626', padding: '1px 7px', borderRadius: 3, fontSize: 11, fontWeight: 700, border: '1px solid #fecaca', cursor: 'pointer' }}>✗</button>
                                      <button type="button" onClick={() => setDemandaEditar(dm)} title="Editar"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}>✏️</button>
                                    </>
                                  ) : (
                                    <button type="button" onClick={() => reabrirDemanda(dm)} title="Reabrir"
                                      style={{ background: '#f3f4f6', color: '#374151', padding: '1px 8px', borderRadius: 3, fontSize: 11, border: '1px solid #d1d5db', cursor: 'pointer' }}>↩</button>
                                  )}
                                  {localStorage.getItem('nomeUsuario') === 'Vagner Freitas' && (
                                    <button type="button" onClick={() => excluirDemanda(dm)} title="Excluir"
                                      style={{ background: 'none', color: '#dc2626', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0, lineHeight: 1 }}>✕</button>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>

            {erro && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{erro}</div>}

          </div>
        )}

        {/* ── ABA: VENDA ── */}
        {aba === 'venda' && (
          <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '10px 14px' }}>
            {!(isAdmin() || ehDonoDoCliente(form.consultor)) ? (
              <div style={{ color: '#6b7280', fontSize: 13, padding: 8 }}>Acesso restrito: esta aba é liberada pro administrador e pro consultor dono do cliente.</div>
            ) : !editando ? (
              <div style={{ color: '#6b7280', fontSize: 13, padding: 8 }}>Salve o cliente primeiro para lançar contas a receber.</div>
            ) : (
              <PainelLancamentos tipo="receber" idField="cliente_id" idValue={id} nomeValue={form.nome || form.razao_social || form.responsavel || ''} />
            )}
          </div>
        )}

        {/* ── ABA: DOCUMENTOS ── */}
        {aba === 'documentos' && (
          <AbaDocumentos form={form} processos={processos} clienteId={id} />
        )}

        {/* ── ABA: 💬 MENSAGENS — histórico de WhatsApp do cliente ── */}
        {aba === 'mensagens' && (
          <div style={{ padding: '4px 2px' }}>
            <HistoricoMensagens key={String(msgCliente)} clienteId={paramId || ''}
              aoAgendar={editando ? () => setMsgCliente(true) : null} />
          </div>
        )}

        {/* ── ABA: UPLOAD ── */}
        {aba === 'upload' && (
          !(isAdmin() || ehDonoDoCliente(form.consultor)) ? (
            <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '14px 16px' }}>
              <div style={{ color: '#6b7280', fontSize: 13, padding: 8 }}>
                🔒 Acesso restrito: os arquivos deste cliente são liberados só pro administrador e pro consultor responsável ({form.consultor || 'sem consultor definido'}).
              </div>
            </div>
          ) : (
            <AbaUpload clienteId={id} />
          )
        )}

        {/* ── ABA: TAREFAS ── */}
        {aba === 'tarefas' && (
          <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '10px 14px' }}>
            <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              {!adicionandoTarefa && (
                <BtnPrimary type="button" onClick={() => setAdicionandoTarefa(true)}>+ Nova Tarefa</BtnPrimary>
              )}
            </div>

            {adicionandoTarefa && (
              <div style={{ border: '2px solid #0a1628', borderRadius: 6, padding: 14, marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', marginBottom: 8 }}>Nova Tarefa</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Descrição *</div>
                    <textarea value={novaTarefa.descricao} onChange={e => setNovaTarefa(f => ({ ...f, descricao: e.target.value }))}
                      rows={3} placeholder="Descreva o que precisa ser feito..."
                      style={{ ...inp, height: 'auto', width: '100%', resize: 'vertical', padding: '4px 6px' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Data <span style={{ fontWeight: 400, color: '#9ca3af' }}>(hora opcional)</span></div>
                      <DataHoraInput value={novaTarefa.data_agendada} onChange={v => setNovaTarefa(f => ({ ...f, data_agendada: v }))} inpStyle={inp} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Destinado a</div>
                      <SelectColaborador value={novaTarefa.destinado_a} onChange={v => setNovaTarefa(f => ({ ...f, destinado_a: v }))} placeholder="Selecione..." style={{ marginTop: 0 }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Subagenda</div>
                    <select value={novaTarefa.subagenda || 'cliente'} onChange={e => setNovaTarefa(f => ({ ...f, subagenda: e.target.value }))} style={{ ...inp, width: '100%' }}>
                      {SUBAGENDAS_OP.map(([v, l]) => <option key={v || 'g'} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>O que foi feito</div>
                    <textarea value={novaTarefa.realizado} onChange={e => setNovaTarefa(f => ({ ...f, realizado: e.target.value }))}
                      rows={2} placeholder="Preencherá após realização..."
                      style={{ ...inp, height: 'auto', width: '100%', resize: 'vertical', padding: '4px 6px' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                  <BtnPrimary type="button" onClick={salvarTarefa} disabled={salvandoTarefa}>{salvandoTarefa ? 'Salvando...' : 'Salvar Tarefa'}</BtnPrimary>
                  <BtnCancel type="button" onClick={() => { setAdicionandoTarefa(false); setNovaTarefa({ ...TAREFA_NOVA, destinado_a: localStorage.getItem('nomeUsuario') || '' }); }}>Cancelar</BtnCancel>
                  <button type="button" onClick={() => setNovaTarefa(f => ({ ...f, favorita: !f.favorita }))} title="Favorita: aparece no filtro ⭐ da Agenda em qualquer dia" style={{ marginLeft: 'auto', background: novaTarefa.favorita ? '#fffbeb' : '#fff', color: novaTarefa.favorita ? '#b45309' : '#6b7280', border: `1px solid ${novaTarefa.favorita ? '#fde68a' : '#d1d5db'}`, borderRadius: 4, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{novaTarefa.favorita ? '⭐ Favorita' : '☆ Marcar favorita'}</button>
                  <button type="button" onClick={() => setNovaTarefa(f => ({ ...f, urgente: !f.urgente }))} title="Urgente: linha vermelha + filtro 🔴 da Agenda" style={{ background: novaTarefa.urgente ? '#fee2e2' : '#fff', color: novaTarefa.urgente ? '#b91c1c' : '#6b7280', border: `1px solid ${novaTarefa.urgente ? '#fecaca' : '#d1d5db'}`, borderRadius: 4, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{novaTarefa.urgente ? '🔴 Urgente' : '⚪ Urgente'}</button>
                  <button type="button" onClick={() => setNovaTarefa(f => ({ ...f, lembrar30: !f.lembrar30 }))} title="Você recebe um aviso no WhatsApp 30 minutos antes da hora da tarefa (precisa preencher a hora)" style={{ background: novaTarefa.lembrar30 ? '#eff6ff' : '#fff', color: novaTarefa.lembrar30 ? '#1d4ed8' : '#6b7280', border: `1px solid ${novaTarefa.lembrar30 ? '#bfdbfe' : '#d1d5db'}`, borderRadius: 4, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{novaTarefa.lembrar30 ? '🔔 Aviso 30min antes' : '🔕 Avisar 30min antes'}</button>
                </div>
              </div>
            )}

            {tarefas.length === 0 && !adicionandoTarefa && (
              <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>Nenhuma tarefa cadastrada.</div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tarefas.map(t => (
                <div key={t._id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 14 }}>
                  {editandoTarefa === t._id && (isAdmin() || !t.realizado) ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <textarea value={tarefaEditForm.descricao || ''} onChange={e => setTarefaEditForm(f => ({ ...f, descricao: e.target.value }))} rows={2}
                        style={{ ...inp, height: 'auto', width: '100%', resize: 'vertical', padding: '4px 6px' }} />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <DataHoraInput value={tarefaEditForm.data_agendada || ''} onChange={v => setTarefaEditForm(f => ({ ...f, data_agendada: v }))} inpStyle={inp} />
                        <SelectColaborador value={tarefaEditForm.destinado_a || ''} onChange={v => setTarefaEditForm(f => ({ ...f, destinado_a: v }))} style={{ marginTop: 0 }} />
                      </div>
                      <textarea value={tarefaEditForm.realizado || ''} onChange={e => setTarefaEditForm(f => ({ ...f, realizado: e.target.value }))} rows={2}
                        style={{ ...inp, height: 'auto', width: '100%', resize: 'vertical', padding: '4px 6px' }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <BtnPrimary type="button" onClick={() => salvarEdicaoTarefa(t._id)}>Salvar</BtnPrimary>
                        <BtnCancel type="button" onClick={() => setEditandoTarefa(null)}>Cancelar</BtnCancel>
                        <button type="button" onClick={() => setTarefaEditForm(f => ({ ...f, favorita: !f.favorita }))} title="Favorita: aparece no filtro ⭐ da Agenda em qualquer dia" style={{ marginLeft: 'auto', background: tarefaEditForm.favorita ? '#fffbeb' : '#fff', color: tarefaEditForm.favorita ? '#b45309' : '#6b7280', border: `1px solid ${tarefaEditForm.favorita ? '#fde68a' : '#d1d5db'}`, borderRadius: 4, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{tarefaEditForm.favorita ? '⭐ Favorita' : '☆ Marcar favorita'}</button>
                        <button type="button" onClick={() => setTarefaEditForm(f => ({ ...f, urgente: !f.urgente }))} title="Urgente: linha vermelha + filtro 🔴 da Agenda" style={{ background: tarefaEditForm.urgente ? '#fee2e2' : '#fff', color: tarefaEditForm.urgente ? '#b91c1c' : '#6b7280', border: `1px solid ${tarefaEditForm.urgente ? '#fecaca' : '#d1d5db'}`, borderRadius: 4, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{tarefaEditForm.urgente ? '🔴 Urgente' : '⚪ Urgente'}</button>
                        <button type="button" onClick={() => setTarefaEditForm(f => ({ ...f, lembrar30: !f.lembrar30 }))} title="Aviso no seu WhatsApp 30 minutos antes da hora da tarefa (precisa ter hora)" style={{ background: tarefaEditForm.lembrar30 ? '#eff6ff' : '#fff', color: tarefaEditForm.lembrar30 ? '#1d4ed8' : '#6b7280', border: `1px solid ${tarefaEditForm.lembrar30 ? '#bfdbfe' : '#d1d5db'}`, borderRadius: 4, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{tarefaEditForm.lembrar30 ? '🔔 Aviso 30min antes' : '🔕 Avisar 30min antes'}</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>Criado em: {fmtDt(t.criado_em)}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {t.realizado && !isAdmin() ? (
                            <span style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>🔒 Concluída</span>
                          ) : (
                            <>
                              <button type="button" onClick={() => { setEditandoTarefa(t._id); setTarefaEditForm({ descricao: t.descricao, data_agendada: t.data_agendada, destinado_a: t.destinado_a, realizado: t.realizado, favorita: !!t.favorita, urgente: !!t.urgente, lembrar30: !!(t.lembrete_em && !t.lembrete_enviado) }); }}
                                style={{ background: '#f3f4f6', color: '#374151', padding: '3px 10px', borderRadius: 4, fontSize: 12, border: 'none', cursor: 'pointer' }}>✏️ Editar</button>
                              <button type="button" onClick={() => setConfirmacaoTarefa(t._id)}
                                style={{ background: '#fef2f2', color: '#dc2626', padding: '3px 10px', borderRadius: 4, fontSize: 12, border: 'none', cursor: 'pointer' }}>🗑 Excluir</button>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 10px', marginBottom: 8, fontSize: 13, color: '#111' }}>
                        <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 2 }}>TAREFA</div>
                        {t.descricao}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                        <div style={{ background: '#eff6ff', borderRadius: 6, padding: '6px 10px' }}>
                          <div style={{ fontSize: 10, color: '#3b82f6', fontWeight: 600, marginBottom: 2 }}>DATA/HORA</div>
                          <div style={{ fontSize: 12, color: '#1e40af' }}>{fmtDt(t.data_agendada)}</div>
                          {t.lembrete_em && (
                            <div style={{ fontSize: 10.5, color: t.lembrete_enviado ? '#16a34a' : '#1d4ed8', marginTop: 2 }}
                              title={t.lembrete_enviado ? 'aviso já enviado no WhatsApp' : 'você recebe um aviso no WhatsApp nessa hora'}>
                              {t.lembrete_enviado ? '🔔 avisado' : `🔔 aviso ${fmtDt(t.lembrete_em)}`}
                            </div>
                          )}
                        </div>
                        <div style={{ background: '#f0fdf4', borderRadius: 6, padding: '6px 10px' }}>
                          <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 600, marginBottom: 2 }}>DESTINADO A</div>
                          <div style={{ fontSize: 12, color: '#15803d' }}>{t.destinado_a || '—'}</div>
                        </div>
                      </div>
                      <div style={{ background: t.realizado ? '#f0fdf4' : '#fafafa', borderRadius: 6, padding: '6px 10px', border: `1px solid ${t.realizado ? '#bbf7d0' : '#e5e7eb'}` }}>
                        <div style={{ fontSize: 10, color: t.realizado ? '#16a34a' : '#9ca3af', fontWeight: 600, marginBottom: 2 }}>O QUE FOI FEITO</div>
                        <div style={{ fontSize: 12, color: t.realizado ? '#15803d' : '#9ca3af' }}>{t.realizado || 'Ainda não preenchido'}</div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            </>
          </div>
        )}

      </form>
    </div>
  );
}

// ── Aba Documentos ──────────────────────────────────────────────────────────
const MODELOS_DOC = [
  { key: 'procuracao_cpf',  label: 'Procuração CPF',                icone: '📄' },
  { key: 'procuracao_cnpj', label: 'Procuração CNPJ',               icone: '📄' },
  { key: 'contrato',        label: 'Contrato de Prestação de Serviço', icone: '📋' },
];

const CAMPOS_DOC = {
  procuracao_cpf: [
    { name: 'nome',          label: 'Nome completo',     placeholder: 'Ex: JOÃO DA SILVA', required: true },
    { name: 'cpf',           label: 'CPF',               placeholder: 'Ex: 000.000.000-00', required: true },
    { name: 'profissao',     label: 'Profissão',         placeholder: 'Ex: empresário(a)', required: false },
    { name: 'nacionalidade', label: 'Nacionalidade',     placeholder: 'Ex: brasileiro(a)', required: false },
    { name: 'endereco',      label: 'Endereço completo', placeholder: 'Rua, nº, bairro, cidade - UF', required: true, full: true },
  ],
  procuracao_cnpj: [
    { name: 'nome_empresa',             label: 'Razão Social',               placeholder: 'Ex: EMPRESA LTDA', required: true },
    { name: 'cnpj_empresa',             label: 'CNPJ da Empresa',            placeholder: 'Ex: 00.000.000/0001-00', required: true },
    { name: 'endereco_empresa',         label: 'Endereço da Empresa',        placeholder: 'Rua, nº, bairro, cidade - UF, CEP', required: true, full: true },
    { name: 'nome_representante',       label: 'Nome do Representante',      placeholder: 'Ex: JOÃO DA SILVA', required: true },
    { name: 'cpf_representante',        label: 'CPF do Representante',       placeholder: 'Ex: 000.000.000-00', required: true },
    { name: 'nacionalidade',            label: 'Nacionalidade',              placeholder: 'Ex: brasileiro(a)', required: false },
    { name: 'profissao_representante',  label: 'Profissão do Representante', placeholder: 'Ex: empresário(a)', required: false },
  ],
  contrato: [
    { name: 'tipo',                    label: 'Tipo de pessoa', tipo: 'select', opcoes: [{ v: 'cnpj', l: 'Pessoa Jurídica (CNPJ)' }, { v: 'cpf', l: 'Pessoa Física (CPF)' }], required: true },
    { name: 'nome_contratante',        label: 'Nome / Razão Social',        placeholder: 'Ex: EMPRESA LTDA', required: true },
    { name: 'cpf_cnpj',               label: 'CPF / CNPJ',                 placeholder: 'Ex: 00.000.000/0001-00', required: true },
    { name: 'endereco_contratante',    label: 'Endereço',                   placeholder: 'Rua, nº, bairro, cidade - UF', required: true, full: true },
    { name: 'nome_representante',      label: 'Nome do Representante',      placeholder: 'Ex: MARIA SILVA', required: true },
    { name: 'cpf_representante',       label: 'CPF do Representante',       placeholder: 'Ex: 000.000.000-00', required: true },
    { name: 'nacionalidade',           label: 'Nacionalidade',              placeholder: 'Ex: brasileiro(a)', required: false },
    { name: 'profissao_representante', label: 'Profissão do Representante', placeholder: 'Ex: empresário(a)', required: false },
    { name: 'objeto',                  label: 'Objeto do contrato',         placeholder: 'Ex: DOIS PEDIDOS DE REGISTRO DA MARCA', required: true, full: true },
    { name: 'valor',                   label: 'Valor (R$)',                 placeholder: 'Ex: R$3.200,00', required: true },
    { name: 'valor_extenso',           label: 'Valor por extenso',          placeholder: 'Ex: três mil e duzentos reais', required: true },
    // Mesma opção do Gerador de Arquivos — sem ela o contrato da ficha saía
    // SEMPRE assinado, sem escolha (reclamação do Vagner 12/08)
    { name: 'assinatura',              label: 'Assinatura do Vagner',       tipo: 'select', opcoes: [{ v: 'com', l: '✍️ Com assinatura (sai já assinado)' }, { v: 'sem', l: 'Sem assinatura (imprimir e assinar à mão)' }], required: true, full: true },
  ],
};

function montarEnderecoCliente(form) {
  return [form.rua, form.numero, form.bairro, form.cidade, form.estado].filter(Boolean).join(', ');
}

function preencherComCliente(key, form) {
  const end = montarEnderecoCliente(form);
  if (key === 'procuracao_cpf') return { nome: (form.responsavel || form.nome || '').toUpperCase(), cpf: form.cpf || '', endereco: end };
  if (key === 'procuracao_cnpj') return {
    nome_empresa: (form.razao_social || form.nome || '').toUpperCase(),
    cnpj_empresa: form.cnpj || '',
    endereco_empresa: end,
    nome_representante: (form.responsavel || form.nome || '').toUpperCase(),
    cpf_representante: form.cpf || '',
  };
  if (key === 'contrato') return {
    tipo: form.cnpj ? 'cnpj' : 'cpf',
    nome_contratante: (form.razao_social || form.nome || '').toUpperCase(),
    cpf_cnpj: form.cnpj || form.cpf || '',
    endereco_contratante: end,
    nome_representante: (form.responsavel || form.nome || '').toUpperCase(),
    cpf_representante: form.cpf || '',
    data: new Date().toISOString().split('T')[0],
  };
  return {};
}

function AbaDocumentos({ form, processos, clienteId }) {
  const [modelo, setModelo] = useState(null);
  const [campos, setCampos] = useState({});
  const [gerando, setGerando] = useState(null);
  const [pronto, setPronto] = useState(false);
  // 🖊️ D4Sign: só habilita o botão quando o servidor tem o token configurado
  const [d4, setD4] = useState({ configurado: false, ambiente: 'sandbox' });
  const [assinaturas, setAssinaturas] = useState([]);

  const carregarAssinaturas = useCallback(() => {
    if (!clienteId) return;
    fetch(`/d4sign/assinaturas?cliente_id=${clienteId}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => setAssinaturas(d.assinaturas || [])).catch(() => {});
  }, [clienteId]);

  useEffect(() => {
    fetch('/d4sign/config', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => setD4(d || {})).catch(() => {});
    carregarAssinaturas();
  }, [carregarAssinaturas]);

  async function enviarAssinatura() {
    const email = (form.email || '').trim();
    const whats = (form.telefone || '').replace(/\D/g, '');
    if (!email && !whats) return alert('O cliente precisa ter e-mail ou telefone no cadastro pra receber o convite de assinatura.');
    const quem = form.nome || form.razao_social || 'o cliente';
    if (!window.confirm(`Enviar "${modelo === 'contrato' ? 'Contrato' : 'Procuração'}" pra ${quem} assinar digitalmente?\n\nConvite vai para: ${email || ''}${email && whats ? ' e ' : ''}${whats ? 'WhatsApp ' + whats : ''}`)) return;
    setGerando('assinatura');
    try {
      const r = await fetch('/d4sign/enviar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          tipo: modelo, dados: campos, cliente_id: clienteId,
          signatario: { nome: quem, email, whatsapp: whats, cpf: form.cpf || campos.cpf_representante || campos.cpf || '' },
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro || 'Falha no envio');
      alert('✅ Enviado! O cliente recebe o convite pra assinar. Quando assinar, o PDF assinado entra sozinho na aba Upload e você é avisado no WhatsApp.');
      carregarAssinaturas();
    } catch (e) { alert('Erro ao enviar para assinatura: ' + e.message); }
    finally { setGerando(null); }
  }

  async function conferirAssinatura(id) {
    try {
      const r = await fetch(`/d4sign/assinaturas/${id}/conferir`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro || 'falhou');
      alert(d.status === 'assinado' ? '✅ Assinado! O PDF já está na aba Upload.' : `Situação na D4Sign: ${d.status}`);
      carregarAssinaturas();
    } catch (e) { alert('Não consegui conferir: ' + e.message); }
  }

  function selecionarModelo(key) {
    setModelo(key);
    setCampos({ tipo: 'cnpj', assinatura: 'com', ...preencherComCliente(key, form) });
    setPronto(false);
  }

  async function gerar(formato) {
    setGerando(formato);
    try {
      const res = await fetch(`/documentos/${formato}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ tipo: modelo, dados: campos }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.erro || 'Erro'); }
      const blob = await res.blob();
      const ext = formato === 'pdf' ? 'pdf' : 'docx';
      // Nome do arquivo no MESMO padrão do Gerador de Arquivos:
      // "Contrato FULANO.pdf", não "contrato.pdf"
      const quem = (modelo === 'contrato' ? campos.nome_contratante
        : modelo === 'procuracao_cnpj' ? campos.nome_empresa : campos.nome) || form.nome || '';
      const prefixo = modelo === 'contrato' ? 'Contrato' : 'Procuração';
      const url = URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url; a.download = `${prefixo}${quem ? ' ' + quem : ''}.${ext}`.replace(/[\\/:*?"<>|]/g, '');
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { alert('Erro: ' + e.message); }
    finally { setGerando(null); }
  }

  const inpDoc = { width: '100%', padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', boxSizing: 'border-box', background: '#fff', height: 26 };

  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '10px 14px' }}>
      <SectionHeader>Selecione o Documento</SectionHeader>

      {/* Cards de modelo */}
      <div style={{ display: 'flex', gap: 10, margin: '10px 0 14px' }}>
        {MODELOS_DOC.map(m => (
          <div key={m.key} onClick={() => selecionarModelo(m.key)}
            style={{
              flex: 1, background: modelo === m.key ? '#0a1628' : '#f8fafc',
              border: `2px solid ${modelo === m.key ? '#0a1628' : '#c8d0da'}`,
              borderRadius: 6, padding: '10px 12px', cursor: 'pointer', textAlign: 'center',
            }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{m.icone}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: modelo === m.key ? '#fff' : '#0a1628' }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* Campos do modelo selecionado */}
      {modelo && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, marginBottom: 2 }}>
            <SectionHeader style={{ margin: 0, flex: 1 }}>Dados do Documento</SectionHeader>
            <button type="button"
              onClick={() => setCampos(prev => ({ ...prev, ...preencherComCliente(modelo, form) }))}
              style={{ background: '#0a1628', color: '#fff', padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 10 }}>
              ⬇ Preencher com dados do cliente
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
            {CAMPOS_DOC[modelo].map(c => (
              <div key={c.name} style={{ gridColumn: c.full ? '1 / -1' : 'auto' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>
                  {c.label}{c.required && <span style={{ color: '#dc2626' }}> *</span>}
                </label>
                {c.tipo === 'select' ? (
                  <select value={campos[c.name] || ''} onChange={e => setCampos(p => ({ ...p, [c.name]: e.target.value }))}
                    style={{ ...inpDoc, padding: '2px 4px', height: 26 }}>
                    {c.opcoes.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input type={c.type || 'text'} value={campos[c.name] || ''}
                    onChange={e => setCampos(p => ({ ...p, [c.name]: e.target.value }))}
                    onPaste={colarEmMaiusculas}
                    placeholder={c.placeholder} required={c.required} style={inpDoc} />
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button type="button" onClick={() => setPronto(true)}
              style={{ background: '#0a1628', color: '#fff', padding: '5px 16px', fontSize: 13, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer' }}>
              Preparar documento
            </button>
            <button type="button" onClick={() => { setModelo(null); setPronto(false); }}
              style={{ background: '#f3f4f6', color: '#374151', padding: '5px 14px', fontSize: 13, borderRadius: 4, border: '1px solid #d1d5db', cursor: 'pointer' }}>
              Cancelar
            </button>
          </div>

          {pronto && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>✅ Pronto! Escolha o formato:</span>
              <button type="button" onClick={() => gerar('word')} disabled={!!gerando}
                style={{ background: '#1d4ed8', color: '#fff', padding: '5px 14px', fontSize: 13, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer' }}>
                {gerando === 'word' ? '⏳...' : '📝 Word'}
              </button>
              <button type="button" onClick={() => gerar('pdf')} disabled={!!gerando}
                style={{ background: '#dc2626', color: '#fff', padding: '5px 14px', fontSize: 13, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer' }}>
                {gerando === 'pdf' ? '⏳...' : '📄 PDF'}
              </button>
              {/* 🖊️ Assinatura digital: o cliente assina pelo celular e o PDF
                  assinado volta sozinho pra aba Upload deste cadastro */}
              <button type="button" onClick={enviarAssinatura} disabled={!!gerando || !d4.configurado}
                title={d4.configurado
                  ? 'Envia pro cliente assinar digitalmente (D4Sign) — o assinado volta sozinho pro cadastro'
                  : 'D4Sign ainda não configurado: falta cadastrar o token da API no servidor'}
                style={{ background: d4.configurado ? '#7c3aed' : '#e5e7eb', color: d4.configurado ? '#fff' : '#9ca3af', padding: '5px 14px', fontSize: 13, fontWeight: 600, borderRadius: 4, border: 'none', cursor: d4.configurado ? 'pointer' : 'not-allowed' }}>
                {gerando === 'assinatura' ? '⏳ Enviando...' : '🖊️ Enviar para assinar'}
              </button>
              {!d4.configurado && <span style={{ fontSize: 11, color: '#9ca3af' }}>assinatura digital pendente de configuração</span>}
              {d4.configurado && d4.ambiente === 'sandbox' && <span style={{ fontSize: 11, color: '#b45309' }}>modo teste (sandbox)</span>}
            </div>
          )}
        </>
      )}

      {/* Assinaturas deste cliente */}
      {assinaturas.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid #e8eaed', paddingTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', marginBottom: 6 }}>🖊️ Assinaturas digitais</div>
          {assinaturas.map(a => {
            const cor = a.status === 'assinado' ? '#15803d' : a.status === 'cancelado' ? '#b91c1c' : '#b45309';
            const rot = a.status === 'assinado' ? '✅ Assinado' : a.status === 'cancelado' ? '🚫 Cancelado' : a.status === 'assinando' ? '✍️ Assinando' : '⏳ Aguardando assinatura';
            return (
              <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: 12.5, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: '#0a1628', flex: 1, minWidth: 160 }}>{a.nome_documento}</span>
                <span style={{ color: '#6b7280' }}>{a.signatario?.email || a.signatario?.whatsapp || ''}</span>
                <span style={{ color: cor, fontWeight: 700 }}>{rot}</span>
                {a.status !== 'assinado' && a.status !== 'cancelado' && (
                  <button type="button" onClick={() => conferirAssinatura(a._id)}
                    title="Perguntar agora à D4Sign se já assinaram"
                    style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#374151' }}>🔄 conferir</button>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 5 }}>Quando o cliente assinar, o PDF assinado entra sozinho na aba <b>Upload</b> deste cadastro e você recebe um aviso no WhatsApp.</div>
        </div>
      )}
    </div>
  );
}

// Caixinha do tamanho natural: o index.css dá width 100% e padding a todo input
const CAIXINHA = { margin: 0, padding: 0, width: 'auto', flex: 'none', cursor: 'inherit' };
// Sugestões no "＋ Nova categoria" da aba Upload (dá pra digitar qualquer nome)
const SUGESTOES_CATEGORIA = ['Contrato e procuração', 'Documentos pessoais', 'Pedidos no INPI', 'GRUs e comprovantes', 'Certificados de registro', 'Logomarca', 'Pareceres e recursos'];
const btnChip = { background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', fontSize: 11, color: '#4b5563', lineHeight: 1 };

// (83) 98698-7687 — aceita com ou sem o 55 na frente
function fmtTelefone(d) {
  let n = String(d || '').replace(/\D/g, '');
  if (n.length >= 12 && n.startsWith('55')) n = n.slice(2);
  if (n.length === 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
  if (n.length === 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return n;
}

// ── Aba Upload ───────────────────────────────────────────────────────────────
function AbaUpload({ clienteId }) {
  const [arquivos, setArquivos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [erroUpload, setErroUpload] = useState('');
  const inputRef = React.useRef();

  function fmtTamanho(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function fmtData(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR');
  }

  // 📁 Categorias da aba Upload (pedido 28/10/2026): [{ id, nome, total }]
  const [categorias, setCategorias] = useState([]);

  async function carregar() {
    if (!clienteId) return;
    try {
      const cab = { headers: { Authorization: `Bearer ${token()}` } };
      const [res, rc] = await Promise.all([
        fetch(`/uploads/cliente/${clienteId}`, cab),
        fetch(`/uploads/cliente/${clienteId}/categorias`, cab),
      ]);
      const data = await res.json();
      setArquivos(Array.isArray(data) ? data.sort((a, b) => new Date(b.data) - new Date(a.data)) : []);
      const cats = rc.ok ? await rc.json().catch(() => []) : [];
      setCategorias(Array.isArray(cats) ? cats : []);
    } catch {}
  }

  useEffect(() => { carregar(); }, [clienteId]);

  async function enviarArquivos(files) {
    if (!clienteId) { setErroUpload('Salve o cliente antes de fazer upload.'); return; }
    if (!files || files.length === 0) return;
    setEnviando(true);
    setErroUpload('');
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('arquivos', f));
    try {
      const res = await fetch(`/uploads/cliente/${clienteId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.erro || `Erro ${res.status}`);
      }
      await carregar();
    } catch (e) { setErroUpload('Erro ao enviar: ' + e.message); }
    finally { setEnviando(false); }
  }

  async function excluir(filename) {
    if (!window.confirm('Excluir este arquivo?')) return;
    await fetch(`/uploads/cliente/${clienteId}/${filename}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token()}` },
    });
    await carregar();
  }

  async function baixar(filename, nome) {
    try {
      const res = await fetch(`/uploads/cliente/${clienteId}/${filename}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { setErroUpload('Erro ao baixar: ' + e.message); }
  }

  // ── Pré-visualização ──────────────────────────────────────────────
  // O arquivo chega por fetch autenticado (blob) e o navegador renderiza
  // sozinho: imagem, PDF, áudio e vídeo. O resto oferece só o download.
  const [preview, setPreview] = useState(null); // { nome, url, tipo, arquivo }
  const [carregandoPrev, setCarregandoPrev] = useState('');

  function tipoPreview(nome, mime = '') {
    const ext = (nome.split('.').pop() || '').toLowerCase();
    if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'imagem';
    if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
    if (mime.startsWith('audio/') || ['mp3', 'ogg', 'wav', 'opus', 'm4a'].includes(ext)) return 'audio';
    if (mime.startsWith('video/') || ['mp4', 'webm', 'mov'].includes(ext)) return 'video';
    return '';
  }

  async function visualizar(arq) {
    setCarregandoPrev(arq.arquivo);
    try {
      const res = await fetch(`/uploads/cliente/${clienteId}/${arq.arquivo}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const blob = await res.blob();
      const tipo = tipoPreview(arq.nome, blob.type);
      setPreview({ nome: arq.nome, arquivo: arq.arquivo, tipo, url: URL.createObjectURL(blob) });
    } catch (e) { setErroUpload('Erro ao abrir: ' + e.message); }
    setCarregandoPrev('');
  }

  function fecharPreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  // Baixa TODOS os documentos do cliente num ZIP só
  const [baixandoTudo, setBaixandoTudo] = useState(false);
  async function baixarTudo() {
    setBaixandoTudo(true);
    try {
      const res = await fetch(`/uploads/cliente/${clienteId}/zip`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.erro || `Erro ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'documentos_cliente.zip';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { setErroUpload('Erro ao baixar o lote: ' + e.message); }
    setBaixandoTudo(false);
  }

  // ── 🔗 Link do cliente (Portal do Cliente, pedido 27/10/2026) ──────────
  // O cliente abre o link pessoal, confirma um código que chega no WhatsApp
  // do cadastro e baixa estes documentos sozinho, sem precisar pedir.
  const [portal, setPortal] = useState(null); // { link, nome, telefone, fixo, so_documentos }
  const [copiado, setCopiado] = useState(false);
  const [envioLink, setEnvioLink] = useState(null); // { texto, enviando, erro, feito }

  useEffect(() => {
    if (!clienteId) return;
    fetch(`/uploads/cliente/${clienteId}/portal`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null).then(setPortal).catch(() => {});
  }, [clienteId]);

  const podeReceberCodigo = (portal?.telefone || '').length >= 10;

  // 📁 Categorias: organizam os arquivos e a área do cliente mostra igual.
  // Só o Vagner cria, renomeia, exclui, reordena e põe arquivo em categoria.
  const [novaCat, setNovaCat] = useState(null); // null = fechado; texto = digitando
  const [salvandoCat, setSalvandoCat] = useState(false);
  async function chamarCategorias(caminho, metodo, corpo) {
    setSalvandoCat(true);
    setErroUpload('');
    try {
      const r = await fetch(`/uploads/cliente/${clienteId}/${caminho}`, {
        method: metodo,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: corpo ? JSON.stringify(corpo) : undefined,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.erro || `Erro ${r.status}`);
      return d;
    } catch (e) { setErroUpload(e.message); return null; }
    finally { setSalvandoCat(false); }
  }
  async function criarCategoria() {
    const nome = (novaCat || '').trim();
    if (!nome) return;
    const d = await chamarCategorias('categorias', 'POST', { nome });
    if (d) { setCategorias(d); setNovaCat(null); }
  }
  async function renomearCategoria(c) {
    const nome = window.prompt('Novo nome da categoria:', c.nome);
    if (!nome || !nome.trim() || nome.trim() === c.nome) return;
    const d = await chamarCategorias(`categorias/${c.id}`, 'PUT', { nome });
    if (d) setCategorias(d);
  }
  async function excluirCategoria(c) {
    if (!window.confirm(`Excluir a categoria "${c.nome}"?\n\nNenhum arquivo é apagado: ${c.total === 1 ? 'o arquivo dela fica' : `os ${c.total} arquivos dela ficam`} sem categoria.`)) return;
    const d = await chamarCategorias(`categorias/${c.id}`, 'DELETE');
    if (d) { setCategorias(d); setArquivos(as => as.map(a => a.categoria === c.id ? { ...a, categoria: '' } : a)); }
  }
  async function moverCategoria(c, passo) {
    const ids = categorias.map(x => x.id);
    const i = ids.indexOf(c.id), j = i + passo;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const d = await chamarCategorias('categorias', 'PUT', { ordem: ids });
    if (d) setCategorias(d);
  }
  async function porNaCategoria(arq, categoria) {
    const d = await chamarCategorias('categoria-arquivo', 'PUT', { arquivos: [arq.arquivo], categoria });
    if (d) { setArquivos(as => as.map(a => a.arquivo === arq.arquivo ? { ...a, categoria } : a)); setCategorias(d.categorias); }
  }

  // 👤 Área do cliente: só os arquivos MARCADOS aparecem no portal (pedido
  // 27/10/2026). Desmarcado é o padrão; só o Vagner marca/desmarca.
  const naArea = arquivos.filter(a => a.area_cliente).length;
  const [marcando, setMarcando] = useState(false);

  async function marcarAreaCliente(lista, marcar) {
    setMarcando(true);
    setErroUpload('');
    // muda na tela na hora; a resposta do servidor confirma (ou desfaz)
    const nomes = new Set(lista);
    setArquivos(as => as.map(a => nomes.has(a.arquivo) ? { ...a, area_cliente: marcar } : a));
    try {
      const r = await fetch(`/uploads/cliente/${clienteId}/area-cliente`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ arquivos: lista, marcar }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.erro || `Erro ${r.status}`);
      const liberados = new Set(d.arquivos || []);
      setArquivos(as => as.map(a => ({ ...a, area_cliente: liberados.has(a.arquivo) })));
    } catch (e) {
      setErroUpload('Não deu para mudar a área do cliente: ' + e.message);
      await carregar();
    }
    setMarcando(false);
  }

  // Opções da área do cliente no cadastro (pedido 28/10/2026), só o Vagner muda:
  // 🙈 so_documentos — o cliente vê só os arquivos, sem o andamento dos processos
  // 🔓 sem_codigo — o link pessoal abre direto, sem o código no WhatsApp
  // 📄 sem_pdf — o andamento aparece, mas sem o botão "Baixar o andamento em PDF"
  const [salvandoOpcao, setSalvandoOpcao] = useState(false);
  async function mudarOpcao(campos) {
    if (campos.sem_codigo && !window.confirm('Com esta opção, quem tiver o link entra na área do cliente SEM o código no WhatsApp.\n\nSe o link for repassado, outra pessoa vê os documentos. Para fechar de novo, é só desmarcar.\n\nLigar mesmo assim?')) return;
    setSalvandoOpcao(true);
    setErroUpload('');
    try {
      const r = await fetch(`/uploads/cliente/${clienteId}/portal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(campos),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.erro || `Erro ${r.status}`);
      setPortal(p => ({ ...p, so_documentos: !!d.so_documentos, sem_codigo: !!d.sem_codigo, sem_pdf: !!d.sem_pdf }));
    } catch (e) { setErroUpload('Não deu para mudar a área do cliente: ' + e.message); }
    setSalvandoOpcao(false);
  }

  async function copiarLink() {
    try { await navigator.clipboard.writeText(portal.link); }
    catch { window.prompt('Copie o link:', portal.link); return; }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  function abrirEnvioLink() {
    setEnvioLink({
      texto: `Olá! 👋 Aqui é da *Preserva Marcas*.\n\n${portal.so_documentos ? 'Os documentos do seu cadastro ficam disponíveis neste link' : 'Os documentos do seu cadastro e o andamento dos seus processos no INPI ficam disponíveis neste link'}, para você baixar quando precisar:\n${portal.link}\n\n${portal.sem_codigo ? 'É só abrir: não precisa de código nem de senha. O link é pessoal, não repasse. 🔐' : 'É só abrir, tocar em *Receber código no WhatsApp* e digitar o código que chega aqui. Não precisa de senha. 🔐'}`,
      enviando: false, erro: '', feito: false,
    });
  }

  // Vai pela fila da Comunicação: sai do WhatsApp da empresa na hora e fica
  // no histórico da aba 💬 Mensagens (a fila retenta se a sessão cair)
  async function enviarLink() {
    if (!envioLink.texto.includes(portal.link)) {
      setEnvioLink(e => ({ ...e, erro: 'A mensagem precisa ter o link — é por ele que o cliente entra.' }));
      return;
    }
    setEnvioLink(e => ({ ...e, enviando: true, erro: '' }));
    const r = await fetch('/comunicacao/fila', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({
        cliente_id: clienteId,
        cliente_nome: portal.nome,
        telefone: portal.telefone,
        template_id: '',
        template_titulo: '🔗 Link dos documentos',
        texto_final: envioLink.texto.trim(),
        anexos: [],
        agendado_para: new Date().toISOString(),
      }),
    }).then(x => x.json()).catch(() => ({ erro: 'Falha ao enviar. Tente de novo.' }));
    if (r.erro) { setEnvioLink(e => ({ ...e, enviando: false, erro: r.erro })); return; }
    setEnvioLink(e => ({ ...e, enviando: false, feito: true }));
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '14px 16px' }}>
      {envioLink && portal && (
        <div onClick={() => !envioLink.enviando && setEnvioLink(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 480, maxWidth: '94vw', border: '1px solid #c8d0da' }}>
            <div style={{ background: '#0a1628', padding: '8px 14px', borderRadius: '6px 6px 0 0', color: '#fff', fontWeight: 700, fontSize: 14 }}>
              📲 Enviar o link ao cliente
            </div>
            <div style={{ padding: 14 }}>
              {envioLink.feito ? (
                <>
                  <div style={{ fontSize: 14, color: '#15803d', fontWeight: 700 }}>✅ Mensagem enviada para a fila</div>
                  <div style={{ fontSize: 12.5, color: '#374151', marginTop: 6 }}>
                    Sai do WhatsApp da empresa em instantes e fica registrada na aba 💬 Mensagens deste cliente.
                  </div>
                  <div style={{ textAlign: 'right', marginTop: 12 }}>
                    <button type="button" onClick={() => setEnvioLink(null)}
                      style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Fechar</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12.5, color: '#374151', marginBottom: 8 }}>
                    Para <b>{fmtTelefone(portal.telefone)}</b>, o WhatsApp do cadastro{portal.nome ? ` (${portal.nome})` : ''}.{portal.sem_codigo ? ' O link abre direto, sem código.' : ' O código de acesso também chega nesse número.'}
                  </div>
                  {naArea === 0 && (
                    <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginBottom: 8 }}>
                      {portal.so_documentos
                        ? '⚠ Nenhum arquivo está marcado e o andamento está escondido: a área do cliente vai aparecer vazia.'
                        : '⚠ Nenhum arquivo está marcado como Área do cliente: por enquanto ele vai ver só o andamento dos processos.'}
                    </div>
                  )}
                  <textarea value={envioLink.texto} rows={10}
                    onChange={e => setEnvioLink(s => ({ ...s, texto: e.target.value, erro: '' }))}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
                  {envioLink.erro && (
                    <div style={{ color: '#dc2626', fontSize: 12, fontWeight: 600, marginTop: 6 }}>⚠ {envioLink.erro}</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                    <button type="button" onClick={() => setEnvioLink(null)} disabled={envioLink.enviando}
                      style={{ background: '#fff', color: '#374151', border: '1px solid #c8d0da', borderRadius: 4, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Cancelar</button>
                    <button type="button" onClick={enviarLink} disabled={envioLink.enviando}
                      style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 700, opacity: envioLink.enviando ? .6 : 1 }}>
                      {envioLink.enviando ? 'Enviando…' : '📲 Enviar agora'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {preview && (
        <div onClick={fecharPreview} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: preview.tipo === 'pdf' ? 'min(900px, 96vw)' : 'auto', maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview.nome}</div>
              <button type="button" onClick={() => baixar(preview.arquivo, preview.nome)}
                style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Baixar</button>
              <button type="button" onClick={fecharPreview}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280', padding: '0 2px' }}>✕</button>
            </div>
            <div style={{ overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', minHeight: 120 }}>
              {preview.tipo === 'imagem' && <img src={preview.url} alt={preview.nome} style={{ maxWidth: '94vw', maxHeight: '80vh', display: 'block' }} />}
              {preview.tipo === 'pdf' && <iframe title={preview.nome} src={preview.url} style={{ width: '100%', height: '80vh', border: 'none', background: '#fff' }} />}
              {preview.tipo === 'audio' && <audio src={preview.url} controls autoPlay style={{ margin: 24, width: 'min(420px, 80vw)' }} />}
              {preview.tipo === 'video' && <video src={preview.url} controls autoPlay style={{ maxWidth: '94vw', maxHeight: '80vh' }} />}
              {!preview.tipo && (
                <div style={{ padding: 28, fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
                  Este formato não tem pré-visualização no navegador.<br />Use o botão <b>Baixar</b> pra abrir no aplicativo certo.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <SectionHeader>Documentos do Cliente</SectionHeader>

      {portal && (
        <div style={{ marginTop: 8, border: '1px solid #c8d0da', background: '#f8fafc', borderRadius: 6, padding: '9px 12px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628' }}>
            🔗 Link do cliente{' '}
            <span style={{ fontSize: 11.5, fontWeight: 400, color: '#4b5563' }}>
              — ele abre{portal.sem_codigo ? ' (direto, sem código)' : ', confirma um código no WhatsApp'} e baixa sozinho os arquivos marcados como <b>Área do cliente</b> ({portal.so_documentos ? 'o andamento dos processos fica escondido' : 'vê também o andamento dos processos'}).
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <input readOnly value={portal.link} onFocus={e => e.target.select()}
              style={{ flex: 1, minWidth: 220, padding: '4px 8px', fontSize: 12, border: '1px solid #b0b8c4', borderRadius: 4, background: '#fff', color: '#374151' }} />
            <button type="button" onClick={copiarLink}
              style={{ background: '#fff', color: '#0a1628', border: '1px solid #c8d0da', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {copiado ? '✅ Copiado' : '📋 Copiar link'}
            </button>
            {/* Envio pelo WhatsApp da empresa = fila da Comunicação, que é só do Vagner */}
            {isAdmin() && (
              <button type="button" onClick={abrirEnvioLink} disabled={!podeReceberCodigo}
                title={podeReceberCodigo ? 'Manda o link pro WhatsApp do cadastro' : 'Cadastro sem telefone com DDD'}
                style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 12, cursor: podeReceberCodigo ? 'pointer' : 'not-allowed', fontWeight: 600, whiteSpace: 'nowrap', opacity: podeReceberCodigo ? 1 : .5 }}>
                📲 Enviar no WhatsApp
              </button>
            )}
          </div>
          {portal.sem_codigo ? (
            <div style={{ fontSize: 11, marginTop: 5, color: '#b45309', fontWeight: 600 }}>
              🔓 Sem código: o link abre direto a área do cliente — trate o link como uma senha.
            </div>
          ) : (
            <div style={{ fontSize: 11, marginTop: 5, color: podeReceberCodigo && !portal.fixo ? '#6b7280' : '#b45309', fontWeight: podeReceberCodigo && !portal.fixo ? 400 : 600 }}>
              {!podeReceberCodigo
                ? '⚠ Cadastro sem telefone com DDD: o código de acesso não tem para onde ir. Preencha o telefone na aba Identificação.'
                : portal.fixo
                  ? `⚠ O telefone do cadastro parece fixo, ${fmtTelefone(portal.telefone)}: o código pode não chegar no WhatsApp.`
                  : `O código de acesso vai para o WhatsApp ${fmtTelefone(portal.telefone)}, o do cadastro.`}
            </div>
          )}
          {(arquivos.length > 0 || portal.so_documentos) && (
            <div style={{ fontSize: 11, marginTop: 3, color: naArea ? '#15803d' : '#b45309', fontWeight: 600 }}>
              {naArea
                ? `👤 ${naArea} de ${arquivos.length} arquivo${arquivos.length > 1 ? 's' : ''} na área do cliente.`
                : portal.so_documentos
                  ? '⚠ Nenhum arquivo marcado e o andamento está escondido: a área do cliente vai aparecer vazia.'
                  : '⚠ Nenhum arquivo marcado como Área do cliente: por enquanto ele vê só o andamento dos processos.'}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 7 }}>
            {[
              ['so_documentos', '🙈 Esconder o andamento dos processos (o cliente vê só os documentos)', 'Marcado: na área do cliente aparecem só os documentos, sem o andamento dos processos'],
              ['sem_codigo', '🔓 Entrar sem o código no WhatsApp (o link abre direto)', 'Marcado: quem tiver o link entra direto, sem código — desmarque se o link for repassado'],
              ['sem_pdf', '📄 Esconder o botão "Baixar o andamento em PDF"', 'Marcado: o cliente vê o andamento, mas não baixa o PDF dele'],
            ].map(([campo, rotulo, dica]) => {
              // com o andamento escondido, o botão do PDF já não aparece
              const inutil = campo === 'sem_pdf' && portal.so_documentos;
              return (
                <label key={campo} title={!isAdmin() ? 'Só o Vagner muda o que aparece na área do cliente' : inutil ? 'Com o andamento escondido, o botão do PDF já não aparece' : dica}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, alignSelf: 'flex-start', fontSize: 12, fontWeight: 600, userSelect: 'none',
                    color: inutil ? '#9ca3af' : portal[campo] ? '#b45309' : '#374151', cursor: isAdmin() && !salvandoOpcao && !inutil ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={!!portal[campo]} disabled={!isAdmin() || salvandoOpcao || inutil}
                    onChange={e => mudarOpcao({ [campo]: e.target.checked })} style={CAIXINHA} />
                  {rotulo}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {erroUpload && (
        <div style={{ marginTop: 8, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
          ⚠ {erroUpload}
        </div>
      )}

      {/* Zona de drop */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); enviarArquivos(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        style={{
          marginTop: 10, border: `2px dashed ${dragOver ? '#0a1628' : '#c8d0da'}`,
          borderRadius: 8, padding: '28px 16px', textAlign: 'center', cursor: 'pointer',
          background: dragOver ? '#f0f4ff' : '#fafbfc', transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 6 }}>📁</div>
        <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
          {enviando ? 'Enviando...' : 'Arraste arquivos aqui ou clique para selecionar'}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Qualquer formato · Máx. 50 MB por arquivo</div>
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
          onChange={e => enviarArquivos(e.target.files)} />
      </div>

      {!clienteId && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
          Salve o cliente primeiro para habilitar o upload.
        </div>
      )}

      {/* Lista de arquivos */}
      {arquivos.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <SectionHeader>Arquivos enviados ({arquivos.length})</SectionHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isAdmin() && (
                <button type="button" disabled={marcando}
                  onClick={() => marcarAreaCliente(arquivos.map(a => a.arquivo), naArea < arquivos.length)}
                  title={naArea < arquivos.length ? 'Libera todos os arquivos na área do cliente' : 'Tira todos os arquivos da área do cliente'}
                  style={{ background: '#fff', color: '#15803d', border: '1px solid #86efac', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', opacity: marcando ? .6 : 1 }}>
                  {naArea < arquivos.length ? '👤 Marcar todos' : '👤 Desmarcar todos'}
                </button>
              )}
              <button type="button" onClick={baixarTudo} disabled={baixandoTudo}
                title="Baixa todos os documentos deste cliente num único arquivo ZIP"
                style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', opacity: baixandoTudo ? .6 : 1 }}>
                {baixandoTudo ? 'Compactando…' : `📦 Baixar tudo (${arquivos.length})`}
              </button>
            </div>
          </div>
          {/* 📁 Categorias: chips com o total; o Vagner cria, renomeia, reordena e exclui */}
          {(categorias.length > 0 || isAdmin()) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '6px 0 8px', fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: '#0a1628' }}>📁 Categorias:</span>
              {categorias.length === 0 && (
                <span style={{ color: '#9ca3af' }}>nenhuma ainda — crie para organizar os arquivos (o cliente vê a mesma organização)</span>
              )}
              {categorias.map((c, i) => (
                <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '2px 9px', color: '#1e3a8a', fontWeight: 600 }}>
                  {c.nome} <span style={{ color: '#6b7280', fontWeight: 400 }}>· {c.total}</span>
                  {isAdmin() && (
                    <>
                      {i > 0 && <button type="button" title="Mover para antes (a ordem é a mesma na área do cliente)" onClick={() => moverCategoria(c, -1)} disabled={salvandoCat} style={btnChip}>◀</button>}
                      <button type="button" title="Renomear" onClick={() => renomearCategoria(c)} disabled={salvandoCat} style={btnChip}>✎</button>
                      <button type="button" title="Excluir a categoria (os arquivos ficam, sem categoria)" onClick={() => excluirCategoria(c)} disabled={salvandoCat} style={{ ...btnChip, color: '#dc2626' }}>✕</button>
                    </>
                  )}
                </span>
              ))}
              {isAdmin() && (novaCat === null ? (
                <button type="button" onClick={() => setNovaCat('')}
                  style={{ background: '#fff', color: '#1e3a8a', border: '1px dashed #a5b4fc', borderRadius: 999, padding: '2px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                  ＋ Nova categoria
                </button>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <input autoFocus value={novaCat} list="sugestoes-categoria" maxLength={60} placeholder="Nome da categoria"
                    onChange={e => setNovaCat(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') criarCategoria(); if (e.key === 'Escape') setNovaCat(null); }}
                    style={{ width: 210, padding: '3px 8px', fontSize: 12, borderRadius: 4 }} />
                  <datalist id="sugestoes-categoria">{SUGESTOES_CATEGORIA.map(s => <option key={s} value={s} />)}</datalist>
                  <button type="button" onClick={criarCategoria} disabled={salvandoCat || !novaCat.trim()}
                    style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Criar</button>
                  <button type="button" onClick={() => setNovaCat(null)}
                    style={{ background: '#fff', color: '#374151', border: '1px solid #c8d0da', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
                </span>
              ))}
            </div>
          )}
          {(() => {
            // Com categorias, a lista sai agrupada (categoria vazia não aparece
            // aqui, só no chip); sem categorias, lista corrida como sempre
            const ids = new Set(categorias.map(c => c.id));
            const grupos = categorias.length
              ? [...categorias.map(c => ({ id: c.id, nome: c.nome, itens: arquivos.filter(a => a.categoria === c.id) })),
                 { id: '', nome: 'Sem categoria', itens: arquivos.filter(a => !ids.has(a.categoria)) }].filter(g => g.itens.length)
              : [{ id: '', nome: '', itens: arquivos }];
            const linha = (arq, i) => (
              <div key={arq.arquivo} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                // marcado pra área do cliente = linha verdinha
                background: arq.area_cliente ? '#f0fdf4' : (i % 2 === 0 ? '#fafafa' : '#fff'),
                borderBottom: '1px solid #eee',
              }}>
                <span style={{ fontSize: 18 }}>{{ imagem: '🖼️', pdf: '📕', audio: '🎙️', video: '🎬' }[tipoPreview(arq.nome)] || '📄'}</span>
                <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => visualizar(arq)} title="Clique pra pré-visualizar">
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{arq.nome}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtTamanho(arq.tamanho)} · {fmtData(arq.data)}</div>
                </div>
                {isAdmin() && categorias.length > 0 && (
                  <select value={ids.has(arq.categoria) ? arq.categoria : ''} disabled={salvandoCat}
                    onChange={e => porNaCategoria(arq, e.target.value)}
                    title="Categoria do arquivo (o cliente vê a mesma organização)"
                    style={{ width: 'auto', maxWidth: 170, padding: '2px 4px', fontSize: 11.5, borderRadius: 4, border: '1px solid #c8d0da', color: '#374151', background: '#fff' }}>
                    <option value="">Sem categoria</option>
                    {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                )}
                <label title={isAdmin() ? 'Marcado: o cliente vê e baixa este arquivo no Portal do Cliente' : 'Só o Vagner muda o que aparece na área do cliente'}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', userSelect: 'none',
                    color: arq.area_cliente ? '#15803d' : '#6b7280', cursor: isAdmin() && !marcando ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={!!arq.area_cliente} disabled={!isAdmin() || marcando}
                    onChange={e => marcarAreaCliente([arq.arquivo], e.target.checked)}
                    style={CAIXINHA} />
                  Área do cliente
                </label>
                <button type="button" onClick={() => visualizar(arq)} disabled={carregandoPrev === arq.arquivo}
                  title={tipoPreview(arq.nome) ? 'Pré-visualizar sem baixar' : 'Formato sem pré-visualização — abre o aviso'}
                  style={{ background: '#fff', color: '#0a1628', border: '1px solid #c8d0da', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600, opacity: carregandoPrev === arq.arquivo ? .5 : 1 }}>
                  {carregandoPrev === arq.arquivo ? '…' : '👁 Ver'}
                </button>
                <button type="button" onClick={() => baixar(arq.arquivo, arq.nome)}
                  style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                  Baixar
                </button>
                {/* Excluir anexo altera o cadastro: só o Vagner (01/10/2026) */}
                {isAdmin() && (
                  <button type="button" onClick={() => excluir(arq.arquivo)}
                    style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                    Excluir
                  </button>
                )}
              </div>
            );
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {grupos.map(g => (
                  <React.Fragment key={g.id || 'sem-categoria'}>
                    {categorias.length > 0 && (
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 10px 4px', background: '#f1f5f9', borderBottom: '1px solid #e5e7eb' }}>
                        📁 {g.nome} <span style={{ color: '#6b7280', fontWeight: 600 }}>({g.itens.length})</span>
                      </div>
                    )}
                    {g.itens.map(linha)}
                  </React.Fragment>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {arquivos.length === 0 && clienteId && (
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
          Nenhum arquivo enviado ainda.
        </div>
      )}
    </div>
  );
}

// ── Linha densa de campos para Processos ────────────────────────────────────
// Cada campo tem um mínimo (rótulo + valor visível + botão): se a linha não couber, os campos
// descem para a linha de baixo em vez de um ficar por cima do outro. O campo com botão (Classe
// de Nice + 📋 Especificações) ganha o dobro de espaço — sem isso o rótulo e o botão espremiam
// a classe até sumir e o botão invadia o Depósito (04/11/2026).
function PRow({ label, value, onChange, placeholder, extra, full, acao, leitura }) {
  const allFields = [{ label, value, onChange, placeholder }, ...(extra || [])];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #e8eaed', padding: '3px 0', gap: 0, rowGap: 4 }}>
      {allFields.map((field, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div style={{ width: 1, background: '#e8eaed', alignSelf: 'stretch', margin: '0 6px' }} />}
          <div style={{ display: 'flex', alignItems: 'center', flex: i === 0 && full ? 3 : i === 0 && acao ? 2 : 1, gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', paddingRight: 4, flexShrink: 0 }}>{field.label}:</span>
            <input value={field.value || ''} onChange={e => field.onChange(e.target.value)} placeholder={leitura ? '' : (field.placeholder || '')}
              readOnly={!!leitura}
              style={{ width: '100%', minWidth: i === 0 && acao ? 110 : 92, padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', height: 26, boxSizing: 'border-box', background: leitura ? '#f5f6f8' : '#fff' }} />
            {i === 0 && acao && (
              <button type="button" onClick={acao.onClick} title={acao.title}
                style={{ flexShrink: 0, whiteSpace: 'nowrap', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700, cursor: 'pointer', height: 26 }}>
                {acao.rotulo}
              </button>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// Campos da aba Identificação. Também são o formulário do modal da aba Outros
// (outra empresa do mesmo cliente): os dois mostram sempre as mesmas informações.
// cep / cnpj = { buscar, buscando, erro } — botões de consulta de cada tela
// semRelacionamento: esconde Consultor/Indicação/Avaliação/Não Recuperado (modal da aba Outros)
function CamposIdentificacao({ valor, alterar, cadastradoEm, cadastradoDica, cep, cnpj, semRelacionamento = false }) {
  const set = campo => e => alterar(campo, e.target.value);
  const btnConsulta = { whiteSpace: 'nowrap', padding: '2px 10px', fontSize: 12, fontWeight: 600,
    background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', height: 26 };
  return (
    <>
      <SectionHeader>Identificação</SectionHeader>
      <Row fields={[
        { label: 'Fantasia', flex: 3, children: <input value={valor('nome')} onChange={set('nome')} style={inp} required /> },
        { label: 'Cadastrado em', flex: 1, children: <input value={cadastradoEm} title={cadastradoDica} readOnly style={{ ...inp, background: '#f5f5f5', color: '#888' }} /> },
      ]} />
      <Row fields={[
        { label: 'Razão Social', flex: 1, children: <input value={valor('razao_social')} onChange={set('razao_social')} style={inp} /> },
      ]} />
      <Row fields={[
        { label: 'Endereço', flex: 3, children: <input value={valor('rua')} onChange={set('rua')} placeholder="Rua, Avenida..." style={inp} /> },
        { label: 'Número', flex: 1, children: <input value={valor('numero')} onChange={set('numero')} placeholder="Nº" style={inp} /> },
      ]} />
      <Row fields={[
        { label: 'Bairro', flex: 2, children: <input value={valor('bairro')} onChange={set('bairro')} style={inp} /> },
        { label: 'Cidade', flex: 2, children: <input value={valor('cidade')} onChange={set('cidade')} style={inp} /> },
        { label: 'UF', flex: 0.4, children:
          <select value={valor('estado')} onChange={set('estado')} style={{ ...inp, padding: '2px 2px' }}>
            <option value=""></option>
            {ESTADOS_BR.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        },
      ]} />
      <Row fields={[
        { label: 'CEP', flex: 1, children:
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={valor('cep')} onChange={e => { set('cep')(e); cep.buscar(e.target.value); }} placeholder="00000-000" style={{ ...inp, flex: 1 }} />
            <button type="button" onClick={() => cep.buscar()} disabled={cep.buscando} style={btnConsulta}>
              {cep.buscando ? 'Buscando...' : '🔍 Consultar CEP'}
            </button>
          </div>
        },
        { label: 'Telefones', flex: 2, children: <input value={valor('telefone')} onChange={set('telefone')} placeholder="(83) 99999-9999" style={inp} /> },
      ]} />
      {cep.erro && <div style={{ color: '#dc2626', fontSize: 12, padding: '2px 8px' }}>{cep.erro}</div>}
      <Row fields={[
        { label: 'Referência', flex: 1, children: <input value={valor('referencia')} onChange={set('referencia')} placeholder="Ponto de referência..." style={inp} /> },
      ]} />
      <Row fields={[
        { label: 'Outras Info', flex: 1, children: <input value={valor('outras_info')} onChange={set('outras_info')} style={inp} /> },
      ]} />
      <Row fields={[
        { label: 'CNPJ', flex: 1, children: (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={valor('cnpj')} onChange={set('cnpj')} placeholder="00.000.000/0000-00" style={{ ...inp, flex: 1 }} />
            <button type="button" onClick={() => cnpj.buscar()} disabled={cnpj.buscando} style={btnConsulta}>
              {cnpj.buscando ? 'Consultando...' : '🔍 Consultar CNPJ'}
            </button>
          </div>
        )},
      ]} />
      {cnpj.erro && <div style={{ color: '#dc2626', fontSize: 12, padding: '2px 8px' }}>{cnpj.erro}</div>}
      <Row fields={[
        { label: 'E-mail', flex: 2, children: <input type="email" value={valor('email')} onChange={set('email')} placeholder="email@exemplo.com" style={inp} /> },
        { label: 'E-mail Financeiro', flex: 2, children: <input type="email" value={valor('email_financeiro')} onChange={set('email_financeiro')} style={inp} /> },
      ]} />
      <Row fields={[
        { label: 'Responsável', flex: 2, children: <input value={valor('responsavel')} onChange={set('responsavel')} style={inp} /> },
        { label: 'PO', flex: 1, children: <input value={valor('po')} onChange={set('po')} style={inp} /> },
        { label: 'Data Contrato', flex: 1, children: <input type="date" value={valor('data_contrato')} onChange={set('data_contrato')} style={inp} /> },
      ]} />
      <Row fields={[
        { label: 'CPF', flex: 1, children: <input value={valor('cpf')} onChange={set('cpf')} placeholder="000.000.000-00" style={inp} /> },
        { label: 'RG', flex: 1, children: <input value={valor('rg')} onChange={set('rg')} style={inp} /> },
        { label: 'Órgão', flex: 1, children: <input value={valor('orgao')} onChange={set('orgao')} style={inp} /> },
      ]} />
      {!semRelacionamento && <Row fields={[
        { label: 'Consultor', flex: 1, children: isAdmin()
          ? <SelectColaborador value={valor('consultor')} onChange={v => alterar('consultor', v)} style={inp} />
          : <input value={valor('consultor') || '—'} disabled readOnly title="Só o administrador pode trocar o consultor do cliente"
              style={{ ...inp, background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }} /> },
        { label: 'Indicação', flex: 1, children: <input value={valor('indicacao')} onChange={set('indicacao')} style={inp} /> },
        { label: 'Avaliação', flex: 1, children:
          <select value={valor('avaliacao')} onChange={set('avaliacao')} style={{ ...inp, padding: '2px 4px' }}>
            <option value=""></option>
            <option>Ótimo</option><option>Bom</option><option>Regular</option><option>Ruim</option>
          </select>
        },
      ]} />}
      <Row fields={[
        { label: 'Financeiro', flex: 2, children: <input value={valor('financeiro')} onChange={set('financeiro')} style={inp} /> },
        { label: 'Regime Tributário', flex: 1, children:
          <select value={valor('regime_tributario')} onChange={set('regime_tributario')} style={{ ...inp, padding: '2px 4px' }}>
            <option value=""></option>
            <option>MEI</option><option>Simples Nacional</option><option>Lucro Presumido</option><option>Lucro Real</option>
          </select>
        },
      ]} />
      <Row fields={[
        { label: 'Complemento', flex: 2, children: <input value={valor('complemento')} onChange={set('complemento')} placeholder="Apto, sala, bloco..." style={inp} /> },
        ...(semRelacionamento ? [] : [{ label: 'Não Recuperado', flex: 0.5, children:
          <input type="checkbox" checked={!!valor('nao_recuperado')}
            onChange={e => alterar('nao_recuperado', e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer', marginTop: 4 }} />
        }]),
      ]} />

      <SectionHeader style={{ marginTop: 8 }}>Observações</SectionHeader>
      <div style={{ padding: '4px 0' }}>
        <textarea value={valor('observacao')} onChange={set('observacao')} rows={3}
          style={{ ...inp, height: 'auto', resize: 'vertical', width: '100%', padding: '4px 6px' }}
          placeholder="Observações..." />
      </div>
    </>
  );
}

// ── ABA OUTROS: outras empresas do mesmo cliente (pedido 22/10/2026) ───────
// Pode ter muitas: lista compacta (uma linha por empresa) com filtro.
const dataBR = iso => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');

// contarProcessos(id): quantos processos da aba Processos são da empresa;
// aoExcluir(id): devolve esses processos ao cadastro principal
function AbaOutros({ lista, alterarLista, somenteLeitura, nomeCliente, contarProcessos, aoExcluir }) {
  const [aberto, setAberto] = useState(null);     // null | 'novo' | empresa (editar/ver)
  const [excluir, setExcluir] = useState(null);   // empresa a excluir
  const [filtro, setFiltro] = useState('');

  function salvar(dados) {
    if (aberto === 'novo') {
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      alterarLista(prev => [...prev, { ...dados, id, criado_em: new Date().toISOString() }]);
    } else {
      alterarLista(prev => prev.map(o => (o.id === aberto.id ? { ...o, ...dados, atualizado_em: new Date().toISOString() } : o)));
    }
    setAberto(null);
  }

  const sem = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = sem(filtro), qDig = filtro.replace(/\D/g, '');
  const visiveis = lista.filter(o => !q ||
    [o.nome, o.razao_social, o.cidade, o.responsavel].some(v => sem(v).includes(q)) ||
    (qDig.length >= 3 && [o.cnpj, o.cpf, o.telefone].some(v => String(v || '').replace(/\D/g, '').includes(qDig))));

  const th = { padding: '5px 8px', fontSize: 11, fontWeight: 700, color: '#374151', textAlign: 'left', borderBottom: '1px solid #c8d0da', whiteSpace: 'nowrap' };
  const td = { padding: '5px 8px', fontSize: 12.5, color: '#111', borderBottom: '1px solid #eef1f4', verticalAlign: 'top' };
  const btn = { background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 8px', fontSize: 12, cursor: 'pointer' };
  const umaLinha = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }; // muitas empresas: linhas baixas

  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: 'none', padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <SectionHeader style={{ margin: 0, flex: 1 }}>Outras empresas do cliente{lista.length ? ` (${lista.length})` : ''}</SectionHeader>
        {lista.length > 5 && (
          <input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="🔎 Filtrar por nome, CNPJ/CPF, cidade..."
            style={{ ...inp, width: 260 }} />
        )}
        {!somenteLeitura && (
          <button type="button" onClick={() => setAberto('novo')}
            style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            ＋ Criar novo cadastro
          </button>
        )}
      </div>

      {lista.length === 0 ? (
        <div style={{ border: '1px dashed #c8d0da', borderRadius: 6, padding: '22px 16px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
          Nenhuma outra empresa cadastrada para {nomeCliente || 'este cliente'}.
          {!somenteLeitura && <div style={{ marginTop: 4, fontSize: 12 }}>Quando o cliente tiver mais de uma empresa (outro CNPJ ou CPF), use “＋ Criar novo cadastro”.</div>}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>Empresa</th><th style={th}>CNPJ / CPF</th><th style={th}>Cidade</th>
                <th style={th}>Telefone</th><th style={th}>Responsável</th><th style={th} title="Processos da aba Processos vinculados à empresa">Proc.</th><th style={th}>Cadastrado em</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map(o => (
                <tr key={o.id} onClick={() => setAberto(o)} style={{ cursor: 'pointer' }}
                  title={somenteLeitura ? 'Ver o cadastro completo' : 'Abrir para editar'}>
                  {/* width 100% + maxWidth 0: a coluna Empresa fica com o espaço que sobra e corta com "…" */}
                  <td style={{ ...td, width: '100%', maxWidth: 0 }}>
                    <div style={{ ...umaLinha, fontWeight: 700, color: '#0a1628' }} title={o.nome || o.razao_social}>{o.nome || o.razao_social || '(sem nome)'}</div>
                    {o.nome && o.razao_social && <div style={{ ...umaLinha, fontSize: 11.5, color: '#6b7280' }} title={o.razao_social}>{o.razao_social}</div>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{o.cnpj || o.cpf || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{[o.cidade, o.estado].filter(Boolean).join('/') || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{o.telefone || '—'}</td>
                  <td style={td}><div style={{ ...umaLinha, maxWidth: 120 }} title={o.responsavel}>{o.responsavel || '—'}</div></td>
                  <td style={{ ...td, textAlign: 'center' }} title="Processos da aba Processos vinculados a esta empresa">{contarProcessos(o.id) || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dataBR(o.criado_em)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    {somenteLeitura
                      ? <button type="button" onClick={() => setAberto(o)} style={btn}>Ver</button>
                      : <>
                          <button type="button" onClick={() => setAberto(o)} style={btn}>✏️ Editar</button>{' '}
                          <button type="button" onClick={() => setExcluir(o)} style={{ ...btn, color: '#dc2626' }} title="Excluir este cadastro">🗑</button>
                        </>}
                  </td>
                </tr>
              ))}
              {visiveis.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, color: '#6b7280', textAlign: 'center' }}>Nenhuma empresa encontrada com “{filtro}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {aberto && (
        <ModalOutroCadastro inicial={aberto === 'novo' ? null : aberto} somenteLeitura={somenteLeitura}
          nomeCliente={nomeCliente} onSalvar={salvar} onFechar={() => setAberto(null)} />
      )}
      {excluir && (
        <ModalConfirmar mensagem={`Excluir o cadastro de ${excluir.nome || excluir.razao_social || 'esta empresa'}?`}
          extra={contarProcessos(excluir.id) > 0 && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 10px', fontSize: 13, color: '#1e3a8a' }}>
              {contarProcessos(excluir.id) === 1 ? 'O processo vinculado a ela volta' : `Os ${contarProcessos(excluir.id)} processos vinculados a ela voltam`} para o cadastro principal.
            </div>
          )}
          onConfirmar={() => { aoExcluir(excluir.id); alterarLista(prev => prev.filter(o => o.id !== excluir.id)); setExcluir(null); }}
          onCancelar={() => setExcluir(null)} />
      )}
    </div>
  );
}

// Modal da aba Outros: os campos da Identificação + Salvar
function ModalOutroCadastro({ inicial, somenteLeitura, nomeCliente, onSalvar, onFechar }) {
  const [dados, setDados] = useState(() => ({ ...OUTRO_VAZIO, ...(inicial || {}) }));
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroCep, setErroCep] = useState('');
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [erroCnpj, setErroCnpj] = useState('');
  const alterar = (campo, v) => setDados(prev => ({ ...prev, [campo]: v }));
  const temNome = !!(String(dados.nome).trim() || String(dados.razao_social).trim());
  const mudou = JSON.stringify(dados) !== JSON.stringify({ ...OUTRO_VAZIO, ...(inicial || {}) });

  function fechar() {
    if (!somenteLeitura && mudou && !window.confirm('Fechar sem salvar o que foi preenchido?')) return;
    onFechar();
  }
  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') fechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  });

  async function buscarCep(cepVal) {
    const c = (cepVal || dados.cep || '').replace(/\D/g, '');
    if (c.length !== 8) { if (cepVal === undefined) setErroCep('CEP deve ter 8 dígitos.'); return; }
    setErroCep(''); setBuscandoCep(true);
    try {
      const d = await (await fetch(`https://viacep.com.br/ws/${c}/json/`)).json();
      if (d.erro) setErroCep('CEP não encontrado.');
      else setDados(prev => ({ ...prev, rua: d.logradouro || prev.rua, bairro: d.bairro || prev.bairro, cidade: d.localidade || prev.cidade, estado: d.uf || prev.estado }));
    } catch { setErroCep('Erro ao consultar o CEP.'); }
    finally { setBuscandoCep(false); }
  }

  async function buscarCnpj() {
    const cnpj = String(dados.cnpj || '').replace(/\D/g, '');
    if (cnpj.length !== 14) { setErroCnpj('CNPJ deve ter 14 dígitos.'); return; }
    setErroCnpj(''); setBuscandoCnpj(true);
    try {
      const res = await fetch(`/clientes/consultar-cnpj/${cnpj}`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await res.json();
      if (!res.ok) { setErroCnpj(d.erro || 'CNPJ não encontrado na Receita Federal.'); return; }
      setDados(prev => ({
        ...prev,
        razao_social: d.razao_social || prev.razao_social, nome: d.nome_fantasia || prev.nome,
        email: d.email || prev.email,
        telefone: d.telefone ? d.telefone.replace(/\D/g, '').replace(/^(\d{2})(\d+)/, '($1) $2') : prev.telefone,
        cep: d.cep || prev.cep, rua: d.rua || prev.rua, numero: d.numero || prev.numero, complemento: d.complemento || prev.complemento,
        bairro: d.bairro || prev.bairro, cidade: d.cidade || prev.cidade, estado: d.estado || prev.estado,
      }));
    } catch { setErroCnpj('Erro ao consultar a Receita Federal.'); }
    finally { setBuscandoCnpj(false); }
  }

  const titulo = somenteLeitura ? 'Cadastro da empresa' : inicial ? 'Editar cadastro' : 'Novo cadastro';
  return (
    <div onClick={fechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 920, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ background: '#0a1628', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>🏢 {titulo}</div>
            <div style={{ color: '#94a3b8', fontSize: 11.5 }}>Outra empresa de {nomeCliente || 'este cliente'}</div>
          </div>
          <button type="button" onClick={fechar} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          <fieldset disabled={somenteLeitura} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <CamposIdentificacao
              valor={campo => dados[campo] ?? ''}
              alterar={alterar}
              cadastradoEm={inicial ? dataBR(inicial.criado_em) : new Date().toLocaleDateString('pt-BR')}
              cadastradoDica={inicial ? 'Dia em que esta empresa foi cadastrada' : 'Preenchido sozinho ao salvar'}
              cep={{ buscar: buscarCep, buscando: buscandoCep, erro: erroCep }}
              cnpj={{ buscar: buscarCnpj, buscando: buscandoCnpj, erro: erroCnpj }}
              semRelacionamento
            />
          </fieldset>
        </div>
        <div style={{ borderTop: '1px solid #e8eaed', padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
          {!somenteLeitura && !temNome && <span style={{ fontSize: 12, color: '#b45309', marginRight: 'auto' }}>Preencha a Fantasia ou a Razão Social para salvar.</span>}
          <button type="button" onClick={fechar} style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer' }}>
            {somenteLeitura ? 'Fechar' : 'Cancelar'}
          </button>
          {!somenteLeitura && (
            <button type="button" disabled={!temNome} onClick={() => onSalvar(dados)}
              style={{ background: temNome ? '#0a1628' : '#9ca3af', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: temNome ? 'pointer' : 'not-allowed' }}>
              💾 Salvar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ children, style }) {
  return (
    <div style={{ background: '#e8edf2', borderLeft: '3px solid #0a1628', padding: '3px 10px', fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2, ...style }}>
      {children}
    </div>
  );
}

function BtnPrimary({ children, ...props }) {
  return (
    <button {...props} style={{ background: '#0a1628', color: '#fff', padding: '5px 16px', fontSize: 13, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer', ...(props.style || {}) }}>
      {children}
    </button>
  );
}

function BtnSecondary({ children, ...props }) {
  return (
    <button {...props} style={{ background: '#e8edf2', color: '#0a1628', padding: '5px 16px', fontSize: 13, fontWeight: 600, borderRadius: 4, border: '1px solid #0a1628', cursor: 'pointer' }}>
      {children}
    </button>
  );
}

function BtnCancel({ children, ...props }) {
  return (
    <button {...props} style={{ background: '#f3f4f6', color: '#374151', padding: '5px 14px', fontSize: 13, borderRadius: 4, border: '1px solid #d1d5db', cursor: 'pointer' }}>
      {children}
    </button>
  );
}
